import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

import { BuilderIframe, type BuilderIframeHandle } from '@/components/builder/BuilderIframe';
import { getNodeEditorConfig } from '@/components/builder/node-editor-registry';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { useUnsavedChangesBlocker } from '@/hooks/use-unsaved-changes-blocker';
import { useMediaQuery } from '@/hooks/use-media-query';
import { buildExternalEditorUrl } from '@/lib/api/builder-session';
import { useFunnelTemplates, useUpdateFunnelWithRouting } from '@/lib/api/funnels';
import { useIntegrationsList } from '@/lib/api/integrations';
import { useStore } from '@/lib/api/stores';
import { trpc } from '@/lib/api/trpc';
import type { IntegrationProvider } from '@/lib/integrations-constants';
import type { SaveMessage, RequestPreviewMessage, RequestResourcesMessage, PreviewSessionMessage, ResourcesMessage } from '@tagadapay/builder-protocol';

import { ReactFlowProvider } from 'reactflow';
import { EdgeEditorPanel } from './components/EdgeEditorPanel';
import { FunnelConsole } from './components/FunnelConsole';
import { FunnelFlowCanvas } from './components/FunnelFlowCanvas';
import { FunnelHeader } from './components/FunnelHeader';
import { GridEditor } from './components/GridEditor';
import { JsonEditor } from './components/JsonEditor';
import { DirectLinkDialog } from './components/DirectLinkDialog';
import { ManageVariantsDialog } from './components/ManageVariantsDialog';
import { PreviewRequirementsDialog } from './components/PreviewRequirementsDialog';
import { ShopifyEditorPanel } from './components/ShopifyEditorPanel';
import { StepEditorPanel } from './components/StepEditorPanel';
import { FunnelAnalyticsPanel } from './components/analytics';

import { startOfDay, endOfDay, subDays } from 'date-fns';
import type { DateRange } from './components/analytics/FunnelAnalyticsFilters';
import { clampAnalyticsRange, ALL_TIME_START, ALL_TIME_END } from './components/analytics/analyticsRange';
import { useFunnelData } from './hooks/useFunnelData';
import { useFunnelEditorTour } from './hooks/useFunnelEditorTour';
import { useFunnelOfferReminderTour } from './hooks/useFunnelOfferReminderTour';
import { useInstanceScreenshotsForFunnel, resolveStepThumbnailUrl } from './hooks/useInstanceScreenshotsForFunnel';
import { useFunnelHandlers } from './hooks/useFunnelHandlers';
import { useFunnelSave } from './hooks/useFunnelSave';
import { useFunnelSelection } from './hooks/useFunnelSelection';
import { useJsonEditor } from './hooks/useJsonEditor';
import { useVisualEditorUrlSync } from './hooks/useVisualEditorUrlSync';
import type { FunnelNode } from './types';
import { flowStepToNode, nodeToFlowStep } from './types';
import { buildStudioSaveConfig, resolveDeployedPagePath } from './utils/helpers';
import type {
  ForkForEditResult,
  Funnel,
  FunnelConsoleResult,
  FunnelStep,
  PageDefinition,
  PageFeature,
  PageRequirement,
  PageRequirementParam,
} from './types/api';

import { WooCommerceEditorPanel } from './components/WooCommerceEditorPanel';

/**
 * Funnel Editor Page - Refactored for clean code organization
 * Separates concerns into custom hooks and dedicated components
 */
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(FunnelEditorPage, PERMISSIONS.STORES_MANAGE);

function FunnelEditorPage() {
  const { t } = useTranslation('storefront');
  const { id, funnelId } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { storeRoutes, navigate: navigateToStore } = useStoreNavigate();
  const { data: store } = useStore(id || '');
  const { data: integrations } = useIntegrationsList(id || '');
  const integrationUrls = useMemo(() => {
    const urls: Record<string, string> = {};
    if (!integrations) return urls;
    for (const integration of integrations) {
      const url = (integration as any).credentials?.url as string | undefined;
      if (url && integration.provider) {
        urls[integration.provider] = url;
      }
    }
    return urls;
  }, [integrations]);

  // Connectivity comes from the integrations table — `store.config.<provider>.connected`
  // is never written (and StoreDto strips unknown config keys), so gating on it
  // hid the Shopify step settings for every store. #TAG-1748
  const shopifyConnected = useMemo(
    () => integrations?.some((i) => i.provider === 'shopify' && i.enabled) ?? false,
    [integrations],
  );
  const woocommerceConnected = useMemo(
    () =>
      (integrations?.some((i) => i.provider === 'woocommerce' && i.enabled) ?? false) ||
      Boolean(integrationUrls['woocommerce']),
    [integrations, integrationUrls],
  );

  const connectedProviders = useMemo<IntegrationProvider[]>(() => {
    const providers: IntegrationProvider[] = [];
    if (shopifyConnected) providers.push('shopify');
    if (woocommerceConnected) providers.push('woocommerce');
    return providers;
  }, [shopifyConnected, woocommerceConnected]);

  const [consoleResult, setConsoleResult] = useState<FunnelConsoleResult | null>(null);
  const [splitDialogStepId, setSplitDialogStepId] = useState<string | null>(null);
  const [initialEditorTab, setInitialEditorTab] = useState<string | undefined>(undefined);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [directLinkOpen, setDirectLinkOpen] = useState(false);
  // Which checkout the dialog opens on. null = let it default to the first,
  // which is what the funnel-level action does; the per-node shortcut sets it.
  const [directLinkTargetId, setDirectLinkTargetId] = useState<string | null>(null);
  const [analyticsMode, setAnalyticsMode] = useState(false);
  const [analyticsFullscreen, setAnalyticsFullscreen] = useState(false);
  const [analyticsSelectedStepId, setAnalyticsSelectedStepId] = useState<string | null>(null);
  const [analyticsDateRange, setAnalyticsDateRange] = useState<DateRange>(() => ({
    from: startOfDay(subDays(new Date(), 7)),
    to: endOfDay(new Date()),
  }));

  // Drives the analytics layout: a resizable split beside the canvas on
  // desktop, a full-screen overlay on mobile (the 50/50 split squeezed the
  // panel to ~195px on phones, making the numbers unreadable). 768px === the
  // Tailwind `md` breakpoint used by the "desktop recommended" notice.
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const { data: templates, isLoading: loadingTemplates } = useFunnelTemplates(id || '');

  const templatesWithIntegrationStatus = useMemo(() => {
    if (!templates) return [];

    return templates.map((template) => {
      let integrationStatus: {
        provider: IntegrationProvider;
        connected: boolean;
      } | null = null;

      if (template.requirements.includes('shopify_connected')) {
        integrationStatus = {
          provider: 'shopify',
          connected: shopifyConnected,
        };
      } else if (template.requirements.includes('woocommerce_connected')) {
        integrationStatus = {
          provider: 'woocommerce',
          connected: woocommerceConnected,
        };
      }

      return {
        ...template,
        integrationStatus,
      };
    });
  }, [templates, shopifyConnected, woocommerceConnected]);

  // Initialize view mode from URL
  const getInitialViewMode = (): 'visual' | 'flow' | 'json' => {
    // 1. Check URL first (highest priority)
    const urlViewMode = searchParams.get('view');
    if (urlViewMode && ['visual', 'flow', 'json'].includes(urlViewMode)) {
      return urlViewMode as 'visual' | 'flow' | 'json';
    }

    // 2. Default to Flow Editor (ignore localStorage to force default)
    return 'flow';
  };

  // Initialize URL mode from URL
  const getInitialUrlViewMode = (): 'staging' | 'production' => {
    const urlMode = searchParams.get('env');
    if (urlMode && ['staging', 'production'].includes(urlMode)) {
      return urlMode as 'staging' | 'production';
    }
    return 'staging';
  };

  const [viewMode, setViewMode] = useState<'visual' | 'flow' | 'json'>(getInitialViewMode);
  const [urlViewMode, setUrlViewMode] = useState<'staging' | 'production'>(getInitialUrlViewMode);

  // Refs for step boxes (grid editor)
  const stepRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());

  // Screenshot polling state - track which step is waiting
  const [waitingForScreenshot, setWaitingForScreenshot] = useState<string | null>(null);

  // When a funnel is just created (template flow lands here with ?tour=setup),
  // the backend dispatches initial screenshot capture jobs. Poll until they
  // arrive so thumbnails appear without the user clicking Save or Preview.
  const [pollInitialScreenshots, setPollInitialScreenshots] = useState<boolean>(
    () => searchParams.get('tour') === 'setup',
  );

  // Global toggle for showing/hiding preview screenshots
  const [showPreviews, setShowPreviews] = useState(true);

  // A Studio-save recapture is pending for some step (local designSavedAt
  // newer than the displayed capture). Set by an effect below (needs the
  // local funnel state, declared later); drives the poll interval here.
  const [recapturePolling, setRecapturePolling] = useState(false);

  // Fetch specific funnel by ID (with screenshot polling when needed)
  // Uses stale-while-revalidate: show cached data immediately on return from external editor
  const {
    data: apiFunnel,
    isLoading: isFetchingFunnel,
    isFetching: isRefetchingFunnel,
    refetch,
  } = trpc.funnels.getById.useQuery(
    { funnelId: funnelId || '', storeId: id || '' },
    {
      enabled: !!funnelId && !!id,
      refetchInterval: waitingForScreenshot || pollInitialScreenshots || recapturePolling ? 3000 : false,
      staleTime: 30 * 1000,
      refetchOnMount: true,
    },
  );

  const utils = trpc.useUtils();

  // Stop polling after 30 seconds
  useEffect(() => {
    if (!waitingForScreenshot) return;

    const timeout = setTimeout(() => {
      setWaitingForScreenshot(null);
    }, 30000);

    return () => clearTimeout(timeout);
  }, [waitingForScreenshot]);

  // Check if screenshot has appeared and stop polling
  useEffect(() => {
    if (!waitingForScreenshot || !apiFunnel?.config?.nodes) return;

    const waitingNode = apiFunnel.config.nodes.find((node: any) => node.id === waitingForScreenshot);
    if (!waitingNode) return;

    const hasScreenshot =
      waitingNode.metadata?.previewScreenshot ||
      (waitingNode.metadata?.variantScreenshots &&
        Object.keys(waitingNode.metadata.variantScreenshots).length > 0);

    if (hasScreenshot) {
      refetch();
      setWaitingForScreenshot(null);
    }
  }, [apiFunnel, waitingForScreenshot, utils, funnelId, id, refetch]);

  // Safety ceiling for initial-screenshot polling. This is only a fallback for
  // steps that never settle — the auto-stop effect below ends polling as soon
  // as every routable step has either a fresh screenshot OR an error marker.
  // It must be generous: a multi-step funnel's captures (inngest debounce +
  // preview-session build + ~12s page-settle per step + upload, plus queue
  // time) can take a few MINUTES. The old 60s ceiling killed polling — and the
  // "Capturing…" spinner — long before slow captures landed, leaving the
  // editor blank until a manual refocus/refresh. 4 minutes covers realistic
  // capture times while still bounding a stuck poll.
  useEffect(() => {
    if (!pollInitialScreenshots) return;
    const timeout = setTimeout(() => setPollInitialScreenshots(false), 240000);
    return () => clearTimeout(timeout);
  }, [pollInitialScreenshots]);

  // Auto-start initial-screenshot polling when the funnel first loads with at
  // least one routable node missing a preview. Covers the post-creation case
  // where the dialog finishes and the editor mounts before captures complete —
  // user sees the "Capturing..." spinner on those step blocks until the next
  // poll tick lands the thumbnail.
  const pollAutoStartedRef = useRef(false);
  useEffect(() => {
    if (pollAutoStartedRef.current) return;
    if (pollInitialScreenshots) {
      pollAutoStartedRef.current = true;
      return;
    }
    const nodes = apiFunnel?.config?.nodes;
    if (!Array.isArray(nodes) || nodes.length === 0) return;

    const ROUTABLE_STEP_TYPES = new Set([
      'checkout', 'thankyou', 'offer', 'upsell', 'downsell', 'landing',
    ]);
    const anyMissing = nodes.some((node: any) => {
      if (!ROUTABLE_STEP_TYPES.has(node.type)) return false;
      if (node.variants && node.variants.length > 0) {
        const vs = node.metadata?.variantScreenshots || {};
        return node.variants.some((v: any) => !vs[v.id]);
      }
      return !node.metadata?.previewScreenshot?.url;
    });

    if (anyMissing) {
      pollAutoStartedRef.current = true;
      setPollInitialScreenshots(true);
    }
  }, [apiFunnel, pollInitialScreenshots]);

  // 0 = no save yet in this session; the freshness check treats any
  // existing previewScreenshot.capturedAt as fresh enough so the
  // auto-stop can fire as soon as creation-time captures land. After
  // the user actually saves the funnel we update this to Date.now() and
  // freshness becomes a real "captured after save" comparison.
  const [lastSaveAtMs, setLastSaveAtMs] = useState<number>(0);

  // Stop initial-screenshot polling once every routable node is SETTLED — it
  // has either a FRESH screenshot (capturedAt > lastSaveAtMs) OR a capture
  // ERROR marker (the inngest job failed and won't write a screenshot). Both
  // mean "nothing more to wait for" on that step. Settling on the error too is
  // what lets a failed capture fall through to its error/retry state quickly
  // instead of spinning until the 4-minute ceiling.
  //
  // Existence alone (Boolean(url)) isn't enough: after Save on an existing
  // funnel every step still has its OLD screenshot, so a plain existence check
  // would bail immediately and the "Capturing…" spinner would never render.
  // Freshness is gated on lastSaveAtMs; pre-save (creation) it's 0, so any
  // existing capture counts as fresh and polling stops as soon as it lands.
  useEffect(() => {
    if (!pollInitialScreenshots || !apiFunnel?.config?.nodes) return;

    const ROUTABLE_STEP_TYPES = new Set([
      'checkout',
      'thankyou',
      'offer',
      'upsell',
      'downsell',
      'landing',
    ]);
    const routableNodes = apiFunnel.config.nodes.filter((n: any) =>
      ROUTABLE_STEP_TYPES.has(n.type),
    );

    if (routableNodes.length === 0) {
      setPollInitialScreenshots(false);
      return;
    }

    // A capture timestamp (screenshot or error) counts as "settled" when it's
    // newer than the last save — or, pre-save, whenever it exists at all.
    const isAfterSave = (ts: string | undefined | null) => {
      if (!ts) return false;
      if (lastSaveAtMs === 0) return true;
      const ms = new Date(ts).getTime();
      return Number.isFinite(ms) && ms > lastSaveAtMs;
    };

    const allSettled = routableNodes.every((node: any) => {
      if (node.variants && node.variants.length > 0) {
        const vs = node.metadata?.variantScreenshots || {};
        const ve = node.metadata?.variantScreenshotErrors || {};
        return node.variants.every(
          (v: any) => isAfterSave(vs[v.id]?.capturedAt) || isAfterSave(ve[v.id]?.failedAt),
        );
      }
      return (
        isAfterSave(node.metadata?.previewScreenshot?.capturedAt) ||
        isAfterSave(node.metadata?.previewScreenshotError?.failedAt)
      );
    });

    if (allSettled) {
      setPollInitialScreenshots(false);
    }
  }, [apiFunnel, pollInitialScreenshots, lastSaveAtMs]);
  const updateWithRoutingMutation = useUpdateFunnelWithRouting();

  // Fork-for-edit mutation
  const forkForEditMutation = trpc.plugins.forkForEdit.useMutation({
    onSuccess: (result: ForkForEditResult) => {
      utils.plugins.getAvailableInstancesForStepType.invalidate();

      // Navigate to editor with returnUrl
      const searchParams = new URLSearchParams(location.search);
      let urlWithParams = location.pathname;
      if (searchParams.toString()) {
        urlWithParams += `?${searchParams.toString()}`;
      }
      const returnUrl = encodeURIComponent(urlWithParams);
      navigateToStore(`${storeRoutes.EDITOR(result.instanceId)}?returnUrl=${returnUrl}`);

      toast.success(t('funnel.configEditorOpened', { name: result.configName }));
    },
    onError: (error) => {
      toast.error(error.message || t('funnel.failedOpenConfigEditor'));
    },
  });

  // Duplicate design mutation + state
  const [duplicateDialogStepId, setDuplicateDialogStepId] = useState<string | null>(null);
  const [duplicateDesignName, setDuplicateDesignName] = useState('');
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [pendingSaveAfterDuplicate, setPendingSaveAfterDuplicate] = useState(false);

  const duplicateDesignMutation = trpc.plugins.duplicateDesign.useMutation();
  const forkNodeDesignMutation = trpc.funnels.forkNodeDesign.useMutation();

  // Editing a design several stores serve edits it for all of them (TAG-2859):
  // the merchant chooses before the editor opens.
  type SharedDesignChoice = 'edit' | 'copy' | 'cancel';
  const [sharedDesignPrompt, setSharedDesignPrompt] = useState<{
    stores: { id: string; name: string }[];
    resolve: (choice: SharedDesignChoice) => void;
  } | null>(null);
  const askSharedDesign = (stores: { id: string; name: string }[]) =>
    new Promise<SharedDesignChoice>((resolve) => setSharedDesignPrompt((previous) => {
      previous?.resolve('cancel');
      return { stores, resolve };
    }));
  const answerSharedDesign = (choice: SharedDesignChoice) => {
    sharedDesignPrompt?.resolve(choice);
    setSharedDesignPrompt(null);
  };
  const retryStepScreenshotMutation = trpc.funnels.retryStepScreenshot.useMutation();

  const handleDuplicateDesign = async (stepId: string) => {
    const step = findStep(stepId);
    if (!step) return;

    const currentLabel = step.label || step.type || 'Design';
    setDuplicateDesignName(t('funnel.copyOfDesign', { name: currentLabel }));
    setDuplicateDialogStepId(stepId);
  };

  const confirmDuplicateDesign = async () => {
    if (!duplicateDialogStepId || !duplicateDesignName.trim()) return;

    const step = findStep(duplicateDialogStepId);
    if (!step) return;

    const firstVariant = step.variants?.[0];
    const variantConfig = firstVariant?.config;
    const pluginId = variantConfig?.pluginId || variantConfig?.metadata?.pluginId || step.config?.pluginId || step.config?.metadata?.pluginId;
    const deploymentId = variantConfig?.metadata?.deploymentId || step.config?.metadata?.deploymentId;

    if (!pluginId || !deploymentId) {
      toast.error(t('funnel.noDesignToDuplicate'));
      return;
    }

    setIsDuplicating(true);
    try {
      const result = await duplicateDesignMutation.mutateAsync({
        pluginId,
        deploymentId,
        name: duplicateDesignName.trim(),
        storeId: id || '',
        stepType: step.type,
      });

      if (result.success && result.instanceId) {
        // Create new step next to the source
        const sourceNode = funnel.nodes?.find((n) => n.id === duplicateDialogStepId);
        const sourcePosition = sourceNode?.position || { x: 0, y: 0 };

        const newStepId = `step_${Date.now()}`;
        const newStep = {
          id: newStepId,
          label: duplicateDesignName.trim(),
          type: step.type,
          position: { row: 0, col: (funnel.steps?.length || 0) },
          flowPosition: { x: sourcePosition.x + 420, y: sourcePosition.y },
          config: {
            pagePath: result.pagePath || '',
            path: result.pagePath || '',
            instanceId: result.instanceId,
            instanceVersion: '1.0.0',
            variant: duplicateDesignName.trim(),
            pluginId: result.pluginId,
            metadata: {
              pluginId: result.pluginId,
              deploymentId: result.deploymentId,
              internalPath: result.pagePath,
              pageId: result.pageId,
              editorType: 'studio',
            },
          },
          conditions: {},
          connections: [],
        };

        const { node: newNode } = flowStepToNode(newStep);

        setFunnel((prev) => ({
          ...prev,
          steps: [...(prev.steps || []), newStep],
          nodes: [...(prev.nodes || []), newNode],
        }));

        utils.plugins.getAvailableInstancesForStepType.invalidate();

        setSelectedStepId(newStepId);
        // Close dialog but keep isDuplicating=true to show saving overlay
        setDuplicateDialogStepId(null);
        setDuplicateDesignName('');

        // Schedule save after React state settles (funnelRef needs to update)
        setPendingSaveAfterDuplicate(true);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('funnel.failedDuplicateDesign'));
      setIsDuplicating(false);
      setDuplicateDialogStepId(null);
      setDuplicateDesignName('');
    }
  };

  // 🔄 Smart real-time refresh: Track mouse activity for dynamic polling
  const [isHoveringEditor, setIsHoveringEditor] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const analyticsRefetchRef = useRef<(() => void) | null>(null);
  const lastRefetchTimeRef = useRef<number>(0);
  const isHoveringRef = useRef(false); // Track state in ref to avoid effect re-runs

  // 🚀 Defer analytics query to prevent blocking initial page load
  // Analytics is slow (~10s) and would block the entire page if batched with getById
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  useEffect(() => {
    // Defer analytics query by 100ms so it doesn't batch with initial queries
    const timer = setTimeout(() => setAnalyticsEnabled(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleMouseMove = () => {
      const now = Date.now();
      const wasNotHovering = !isHoveringRef.current;

      // Only trigger refetch when transitioning from idle to active
      // AND at least 10 seconds have passed since last refetch (throttle)
      if (wasNotHovering && now - lastRefetchTimeRef.current > 10000) {
        lastRefetchTimeRef.current = now;
        analyticsRefetchRef.current?.();
      }

      isHoveringRef.current = true;
      setIsHoveringEditor(true);

      // Reset timeout - if no mouse movement for 10s, consider user as inactive
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      hoverTimeoutRef.current = setTimeout(() => {
        isHoveringRef.current = false;
        setIsHoveringEditor(false);
      }, 10000); // 10s idle timeout
    };

    // Attach to document to detect any mouse movement on the page
    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []); // No dependencies - use refs instead to avoid re-binding listeners

  // Canvas step metrics mirror the analytics panel's selected period so the
  // canvas and the panel always show the same numbers (no "two truths" on
  // screen). Backend caps range at MAX_ANALYTICS_RANGE_DAYS, so we can no
  // longer send an unbounded "all time" window — the picker's default (last
  // 7 days) is used when the panel hasn't been opened yet.
  // 🔄 Dynamic polling: 15s when active, 60s when idle
  // 🚀 Deferred: analyticsEnabled becomes true after 100ms to avoid batching with initial queries
  // Clamp to the server's max analytics span. Both the all-time fallback
  // (ALL_TIME_START/END) and an "All time" panel selection (epoch sentinel in
  // analyticsDateRange) are wider than the cap and would 400 otherwise.
  const canvasAnalyticsRange = useMemo(
    () => clampAnalyticsRange(
      analyticsMode ? analyticsDateRange.from : ALL_TIME_START,
      analyticsMode ? analyticsDateRange.to : ALL_TIME_END,
    ),
    [analyticsMode, analyticsDateRange.from, analyticsDateRange.to],
  );
  // Declared here (not in the Visual Editor block below) because the
  // analytics query needs it: while the Studio overlay is open the funnel
  // page stays mounted underneath, and this heavy (~10s server-side) query
  // kept polling for results nobody could see (audit FE-02).
  const [visualEditorStepId, setVisualEditorStepId] = useState<string | null>(null);
  // SINGLE source of truth for "the Studio overlay is actually on screen".
  // The overlay only renders in staging, but the page chrome used to hide on
  // `visualEditorStepId` alone — so any state where a step was open in the
  // production view rendered NEITHER, leaving a bare canvas with no header,
  // no toolbar and no way back. Every layout decision now derives from this
  // one flag, so the chrome can never disappear without the editor replacing
  // it. Keep it that way: don't reintroduce raw `visualEditorStepId` checks
  // in layout conditions.
  const isVisualEditorOpen = !!visualEditorStepId && urlViewMode === 'staging';
  // Which A/B variant the open editor is editing. Split-step designs live in
  // variants[].config — the save-back and fork guard must target THAT config,
  // not step.config (writing there corrupts the split step, TAG-1334 gap b).
  const [visualEditorVariantId, setVisualEditorVariantId] = useState<string | null>(null);
  const { data: analyticsData, isLoading: isLoadingAnalytics, refetch: refetchAnalytics } = trpc.analytics.funnelAnalytics.useQuery(
    {
      funnelId: funnelId || '',
      storeIds: id ? [id] : [],
      startDate: canvasAnalyticsRange.from,
      endDate: canvasAnalyticsRange.to,
      environment: urlViewMode,
    },
    {
      enabled: analyticsEnabled && !!funnelId && !!id,
      // Paused entirely while the Studio design editor overlay hides the canvas.
      refetchInterval: visualEditorStepId ? false : isHoveringEditor ? 15000 : 60000, // 15s active / 60s idle
      refetchOnWindowFocus: !visualEditorStepId, // Refresh when user returns to tab
      staleTime: 10000, // Consider data fresh for 10s (reduced from 30s)
    },
  );

  // Store refetch function in ref so it can be called from effect
  useEffect(() => {
    analyticsRefetchRef.current = refetchAnalytics;
  }, [refetchAnalytics]);

  type StepAnalyticsEntry = {
    visitors: number;
    conversions: number;
    progressionRate: number | null;
    progressedToNext: number;
    realtimeVisitors: number;
    conversionValue: number;
    conversionValueUnit: string;
    avgConversionValue: number;
    hasNextStep: boolean;
  };

  const stepAnalytics = useMemo(() => {
    if (!analyticsData?.steps) return undefined;
    const result: Record<string, StepAnalyticsEntry> = {};
    for (const [stepId, analytics] of Object.entries(analyticsData.steps)) {
      result[stepId] = {
        visitors: analytics.visitors,
        conversions: analytics.conversions,
        progressionRate: analytics.progressionRate,
        progressedToNext: analytics.progressedToNext,
        realtimeVisitors: analytics.realtimeVisitors,
        conversionValue: analytics.conversionValue,
        conversionValueUnit: analytics.conversionValueUnit,
        avgConversionValue: analytics.avgConversionValue,
        hasNextStep: analytics.hasNextStep,
      };
    }
    return result;
  }, [analyticsData?.steps]);

  const variantAnalytics = useMemo(() => {
    if (!analyticsData?.variants) return undefined;
    const result: Record<string, StepAnalyticsEntry> = {};
    for (const [variantId, analytics] of Object.entries(analyticsData.variants)) {
      result[variantId] = {
        visitors: analytics.visitors,
        conversions: analytics.conversions,
        progressionRate: analytics.progressionRate,
        progressedToNext: analytics.progressedToNext,
        realtimeVisitors: analytics.realtimeVisitors,
        conversionValue: analytics.conversionValue,
        conversionValueUnit: analytics.conversionValueUnit,
        avgConversionValue: analytics.avgConversionValue,
        hasNextStep: analytics.hasNextStep,
      };
    }
    return result;
  }, [analyticsData?.variants]);

  // Use custom hook for funnel data management
  const {
    funnel,
    setFunnel,
    isLoadingData,
    hasUnsavedChanges,
    updateFunnelFromResult,
    discardChanges,
    staticResources,
    setStaticResources,
    scripts,
    setScripts,
    clearDraft,
  } = useFunnelData(apiFunnel, isFetchingFunnel, urlViewMode, funnelId);

  // ─── Live screenshot sync (no funnel save required) ────────────────
  // A Studio save alone triggers a server-side recapture (deploy hook) that
  // writes straight into funnelsV2 — the funnel save is NOT part of that
  // loop. These two effects close the last hop into an open editor tab:
  //
  // 1. Poll while a recapture is pending: local designSavedAt (stamped at
  //    Studio save-back) newer than the displayed capture. Bounded to 10min
  //    so a lost event can't poll forever.
  useEffect(() => {
    const nodes = funnel?.nodes || [];
    const pending = nodes.some((n: any) => {
      const savedAt = (n?.metadata as any)?.designSavedAt;
      if (!savedAt || Date.now() - savedAt > 10 * 60_000) return false;
      const capturedAt = Date.parse((n?.metadata as any)?.previewScreenshot?.capturedAt || '') || 0;
      return savedAt > capturedAt;
    });
    setRecapturePolling(pending);
  }, [funnel]);

  // 2. Merge server-owned screenshot fields into local state EVEN WHILE the
  //    editor holds unsaved changes. Safe: since TAG-1820 these keys are
  //    exclusively server-written, so pulling them can never clobber an
  //    edit — and nothing else from the server response is touched.
  useEffect(() => {
    const serverNodes = (apiFunnel as any)?.config?.nodes;
    if (!Array.isArray(serverNodes)) return;
    const SHOT_KEYS = ['previewScreenshot', 'previewScreenshotError', 'variantScreenshots', 'variantScreenshotErrors'] as const;
    const serverById = new Map<string, any>(serverNodes.filter((n: any) => n?.id).map((n: any) => [n.id, n]));

    const patchList = (list: any[] | undefined) => {
      if (!Array.isArray(list)) return { list, changed: false };
      let changed = false;
      const next = list.map((item: any) => {
        const server = serverById.get(item?.id);
        if (!server?.metadata) return item;
        const patch: Record<string, any> = {};
        for (const key of SHOT_KEYS) {
          const sv = server.metadata[key];
          if (sv !== undefined && JSON.stringify(sv) !== JSON.stringify((item.metadata || {})[key])) {
            patch[key] = sv;
          }
        }
        if (Object.keys(patch).length === 0) return item;
        changed = true;
        return { ...item, metadata: { ...(item.metadata || {}), ...patch } };
      });
      return { list: next, changed };
    };

    setFunnel((prev) => {
      const nodesRes = patchList(prev.nodes as any[]);
      const stepsRes = patchList(prev.steps as any[]);
      if (!nodesRes.changed && !stepsRes.changed) return prev;
      return {
        ...prev,
        ...(nodesRes.changed ? { nodes: nodesRes.list as any } : {}),
        ...(stepsRes.changed ? { steps: stepsRes.list as any } : {}),
      };
    });
  }, [apiFunnel, setFunnel]);

  // Unified step lookup: checks funnel.steps first, then converts from nodes.
  // Needed because flow canvas creates steps as nodes directly, so funnel.steps
  // may not contain newly created steps until the funnel is saved and reloaded.
  const findStep = useCallback((stepId: string) => {
    const fromSteps = funnel.steps?.find((s) => s.id === stepId);
    if (fromSteps) return fromSteps;
    const node = funnel.nodes?.find((n) => n.id === stepId);
    if (node) return nodeToFlowStep(node, funnel.edges?.filter((e) => e.target === node.id) || []);
    return undefined;
  }, [funnel.steps, funnel.nodes, funnel.edges]);

  // Use custom hook for step selection and URL sync
  const { selectedStepId, setSelectedStepId } = useFunnelSelection({ funnel });

  // ─── Visual Editor (Studio) state ─────────────────────────────────
  // (visualEditorStepId is declared earlier, above the analytics query,
  // which pauses polling while the Studio overlay is open.)
  const builderIframeRef = useRef<BuilderIframeHandle>(null);
  const [builderDirty, setBuilderDirty] = useState(false);
  const [visualEditorDesign, setVisualEditorDesign] = useState<{
    pluginId: string; deploymentId: string; pagePath?: string;
  } | null>(null);
  const [visualEditorSuggestedName, setVisualEditorSuggestedName] = useState<string | undefined>(undefined);
  const [builderSessionToken, setBuilderSessionToken] = useState<string>('');
  const [builderSessionError, setBuilderSessionError] = useState(false);
  const [visualEditorMode, setVisualEditorMode] = useState<'edit' | 'create'>('edit');

  const createBuilderSession = trpc.builder.createSession.useMutation();

  // Guard CRM-side navigation (back button, sidebar links, refresh/close)
  // while the Studio iframe holds unsaved work. Studio's own toolbar Back
  // has its own confirmation; this covers every exit that bypasses it.
  useUnsavedChangesBlocker({
    when: builderDirty && visualEditorStepId !== null,
    onBlock: () => !window.confirm(t('funnel.builderUnsavedLeaveConfirm')),
  });

  // Instance screenshots for funnel nodes (fallback when previewScreenshot not yet captured)
  const instanceScreenshots = useInstanceScreenshotsForFunnel(id || '', funnel.nodes);

  // Prefetch instance metadata when a step is selected (makes preview click instant)
  useEffect(() => {
    if (!selectedStepId || visualEditorStepId) return;
    const step = findStep(selectedStepId);
    if (!step) return;
    const instanceId = step.variants?.[0]?.config?.instanceId ?? step.config?.instanceId;
    if (instanceId) {
      utils.plugins.getInstanceMetadata.prefetch({ instanceId }).catch(() => {});
    }
  }, [selectedStepId, funnel.steps, funnel.nodes, utils, visualEditorStepId]);

  // Callback to reset canvas view (for guided tour)
  const handleResetCanvasView = () => {
    // Dispatch custom event that FunnelFlowCanvas listens to
    window.dispatchEvent(new CustomEvent('funnel-editor-reset-view'));
  };

  const isStorefrontType = (type: string) => type === 'storefront_shopify' || type === 'storefront_woocommerce';

  // Get the first configurable step ID (not storefront types, which have dedicated editors)
  const firstConfigurableStepId = useMemo(() => {
    if (!funnel.nodes || funnel.nodes.length === 0) return undefined;

    const entryStep = funnel.nodes.find((n) => n.isDefault && !isStorefrontType(n.type));
    if (entryStep) return entryStep.id;

    const configurableStep = funnel.nodes.find(
      (n) => !isStorefrontType(n.type) && n.type !== 'thankyou' && n.kind !== 'condition',
    );
    return configurableStep?.id;
  }, [funnel.nodes]);

  // Callback to select first configurable step (for guided tour)
  const handleSelectFirstStep = () => {
    if (firstConfigurableStepId) {
      setSelectedStepId(firstConfigurableStepId);
    }
  };

  // Store save callback ref for tour (will be set after useFunnelSave)
  const tourSaveCallbackRef = useRef<(() => void) | null>(null);

  // Callback to show preview button (for guided tour)
  const handleShowPreviewButton = () => {
    window.dispatchEvent(new CustomEvent('funnel-editor-show-preview-button'));
  };

  // Check if the first configurable step has a design selected
  const checkHasDesignSelected = () => {
    if (!firstConfigurableStepId || !funnel.nodes) return false;
    const step = funnel.nodes.find((n) => n.id === firstConfigurableStepId);
    if (!step) return false;
    // Check if the step has a variant (design) configured
    return !!step.config?.variant;
  };

  // Use custom hook for funnel editor guided tour
  const { onTourStepChange } = useFunnelEditorTour({
    funnelId,
    nodes: funnel.nodes,
    isLoading: isLoadingData || isFetchingFunnel,
    onResetView: handleResetCanvasView,
    onSelectFirstStep: handleSelectFirstStep,
    onSave: () => tourSaveCallbackRef.current?.(),
    onShowPreviewButton: handleShowPreviewButton,
    hasDesignSelected: checkHasDesignSelected,
  });

  // Two-step offer reminder tour — fires after the editor-setup tour is
  // done (or if it never had to run), only when the funnel has an offer
  // step or BOGO checkout.
  const { onTourStepChange: onOfferReminderStepChange } = useFunnelOfferReminderTour({
    funnelId,
    nodes: funnel.nodes,
    isLoading: isLoadingData || isFetchingFunnel,
    onResetView: handleResetCanvasView,
    onSelectStep: setSelectedStepId,
  });

  // Wire up tour step change to global event (for NextStepReact). Both
  // funnel-side tours share the same custom event — fan out to each hook.
  useEffect(() => {
    const handleStepChange = (e: CustomEvent<{ step: number; tourName: string | null }>) => {
      onTourStepChange(e.detail.step, e.detail.tourName);
      onOfferReminderStepChange(e.detail.step, e.detail.tourName);
    };
    window.addEventListener('funnel-tour-step-change', handleStepChange as EventListener);
    return () => window.removeEventListener('funnel-tour-step-change', handleStepChange as EventListener);
  }, [onTourStepChange, onOfferReminderStepChange]);

  // Note: URL sync is handled by useFunnelSelection hook - no duplicate logic here!

  // Visual editor URL sync — allows sharing direct links to Studio editor
  const { setEditorParams, clearEditorParams, pendingRestore, consumeRestore } = useVisualEditorUrlSync({
    isFunnelLoaded: !isFetchingFunnel && ((funnel.steps?.length ?? 0) > 0 || (funnel.nodes?.length ?? 0) > 0),
    steps: funnel.steps || [],
  });

  const getProductionStepUrl = (stepId: string): string | undefined => {
    const extractUrl = (step: { config?: { url?: string }; variants?: { config?: { url?: string } }[] } | undefined): string | undefined => {
      if (!step) return undefined;
      return step.variants?.[0]?.config?.url ?? step.config?.url;
    };

    // Try raw apiFunnel production config first, then processed funnel
    for (const config of [
      (apiFunnel as Funnel | undefined)?.productionConfig,
      funnel?.productionConfig,
    ]) {
      if (!config) continue;
      const url = extractUrl(config.nodes?.find((n: FunnelNode) => n.id === stepId))
        ?? extractUrl(config.steps?.find((s: FunnelStep) => s.id === stepId));
      if (url) return url;
    }

    return undefined;
  };

  /**
   * Checkout steps a direct link can point at.
   *
   * Deliberately NOT filtered on `isDefault`: that flag marks where a session
   * starts when nothing names a step, not where cold traffic may land. A funnel
   * no longer needs a flagged start page (#TAG-2775).
   * A mounted instance IS required — without one the step has no page to open.
   */
  const directLinkTargets = useMemo(() => {
    const nodes = funnel?.nodes || [];
    const checkouts = nodes.filter((n: FunnelNode) => n.type === 'checkout');
    const mounted = checkouts.filter(
      (n: FunnelNode) => n.config?.instanceId || n.variants?.[0]?.config?.instanceId,
    );

    return {
      hasCheckoutStep: checkouts.length > 0,
      targets: mounted.map((n: FunnelNode) => {
        const config = n.variants?.[0]?.config ?? n.config;
        return {
          id: n.id,
          label: n.name || n.id,
          path: config?.pagePath ?? config?.path ?? undefined,
          thumbnailUrl: resolveStepThumbnailUrl(n, instanceScreenshots),
          checkoutUrl: n.variants?.[0]?.config?.url ?? n.config?.url,
          productionCheckoutUrl: getProductionStepUrl(n.id),
        };
      }),
    };
    // getProductionStepUrl reads the same two configs this already depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [funnel?.nodes, funnel?.productionConfig, apiFunnel, instanceScreenshots]);

  // Explains the disabled state rather than leaving a dead button in the header.
  const directLinkDisabledReason = directLinkTargets.targets.length > 0
    ? undefined
    : directLinkTargets.hasCheckoutStep
      ? t('funnelHeader.directLinkNeedsDesign')
      : t('funnelHeader.directLinkNoCheckout');

  // 🛡️ Auto-switch to staging before any edit operation
  const ensureStagingMode = () => {
    if (urlViewMode === 'production') {
      toast.error(t('funnel.switchingToStaging'));
      setUrlViewMode('staging');
      return false; // Block the operation
    }
    return true; // Allow the operation
  };

  // Use custom hook for funnel handlers (CRUD operations)
  const {
    handleStepChange,
    handleCopyPixelsToAllSteps,
    handleAddConditionalStep,
    handleTransformToSplit,
    handleUpdateVariants,
    handleRemoveSplit,
    handleAddColumn,
    handleStepDelete,
  } = useFunnelHandlers({
    funnel,
    setFunnel,
    selectedStepId,
    setSelectedStepId,
  });

  // Wrap handler so it only runs in staging mode
  const guardStaging = <T extends (...args: any[]) => any>(fn: T) =>
    (...args: Parameters<T>): ReturnType<T> | undefined =>
      ensureStagingMode() ? fn(...args) : undefined;

  const protectedHandlers = {
    handleStepChange: guardStaging(handleStepChange),
    handleCopyPixelsToAllSteps: guardStaging(handleCopyPixelsToAllSteps),
    handleAddConditionalStep: guardStaging(handleAddConditionalStep),
    handleTransformToSplit: guardStaging(handleTransformToSplit),
    handleUpdateVariants: guardStaging(handleUpdateVariants),
    handleRemoveSplit: guardStaging(handleRemoveSplit),
    handleAddColumn: guardStaging(handleAddColumn),
    handleStepDelete: guardStaging(handleStepDelete),
  };

  // Use custom hook for save logic
  const { handleSaveFunnel, isSaving, stepsMissingDesign } = useFunnelSave({
    funnel,
    funnelId,
    storeId: id,
    updateWithRoutingMutation,
    updateFunnelFromResult,
    setConsoleResult,
    refetch,
    // Note: staticResources and scripts are now stored in node.config.stepConfig
  });

  // Whenever a save finishes, kick off (or refresh) polling for screenshot
  // captures: saveFunnel fires `funnel/step.screenshot.requested` events for
  // any step whose config changed or has no preview yet, and the inngest fn
  // writes node.metadata.previewScreenshot when it completes. Polling makes
  // the step blocks pick up the new thumbnails without the user having to
  // reload the page. lastSaveAtMs gates the auto-stop effect below so polling
  // doesn't bail immediately just because every step still has its OLD
  // screenshot — it waits for screenshots whose capturedAt > lastSaveAtMs.
  const prevIsSavingRef = useRef(false);
  useEffect(() => {
    if (prevIsSavingRef.current && !isSaving) {
      setLastSaveAtMs(Date.now());
      setPollInitialScreenshots(true);
    }
    prevIsSavingRef.current = isSaving;
  }, [isSaving]);

  // Update tour save callback ref now that handleSaveFunnel is available
  tourSaveCallbackRef.current = () => handleSaveFunnel(urlViewMode);

  // Auto-save after duplicate design (waits for React state + funnelRef to settle)
  useEffect(() => {
    if (!pendingSaveAfterDuplicate) return;
    setPendingSaveAfterDuplicate(false);
    (async () => {
      try {
        await handleSaveFunnel(urlViewMode);
        toast.success(t('funnel.designDuplicatedAndSaved'));
      } catch {
        toast.error(t('funnel.designDuplicatedSaveFailed'));
      } finally {
        setIsDuplicating(false);
      }
    })();
  }, [pendingSaveAfterDuplicate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Use custom hook for JSON editor
  const { jsonValue, setJsonValue, handleApplyJson, handleCopyJson } = useJsonEditor({
    funnel,
    setFunnel,
    viewMode,
    setViewMode,
  });

  // Auto-scroll to selected step (grid editor only)
  useEffect(() => {
    if (viewMode === 'visual' && selectedStepId && stepRefsMap.current.has(selectedStepId)) {
      const stepElement = stepRefsMap.current.get(selectedStepId);
      if (stepElement) {
        stepElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      }
    }
  }, [selectedStepId, viewMode]);

  // Sync viewMode with URL and localStorage
  useEffect(() => {
    const currentView = searchParams.get('view') || 'flow';
    if (viewMode !== currentView) {
      const newParams = new URLSearchParams(searchParams);
      if (viewMode !== 'flow') {
        newParams.set('view', viewMode);
      } else {
        newParams.delete('view');
      }
      setSearchParams(newParams, { replace: true });
    }

    // (No localStorage persistence: the read side deliberately ignores it to
    // force the Flow default — the old write here was dead weight, FE-18.)
  }, [viewMode]);

  // Sync urlViewMode with URL
  useEffect(() => {
    const currentEnv = searchParams.get('env') || 'staging';
    if (urlViewMode !== currentEnv) {
      const newParams = new URLSearchParams(searchParams);
      if (urlViewMode !== 'staging') {
        newParams.set('env', urlViewMode);
      } else {
        newParams.delete('env');
      }
      setSearchParams(newParams, { replace: true });
    }
  }, [urlViewMode]);

  // 🛡️ Auto-redirect to staging if trying to view production but funnel has no production config
  useEffect(() => {
    if (urlViewMode === 'production' && apiFunnel && !apiFunnel.productionConfig) {
      console.warn('[FunnelEditor] ⚠️ No production config found, redirecting to staging');
      toast.error(t('funnel.notPromotedToProduction'));
      setUrlViewMode('staging');
    }
  }, [urlViewMode, apiFunnel]);

  // 🛡️ Open pending split dialog from SessionStorage (robust against URL sync issues)
  useEffect(() => {
    if (urlViewMode === 'staging') {
      try {
        const pendingStepId = sessionStorage.getItem('pending_split_dialog');
        if (pendingStepId) {
          setSplitDialogStepId(pendingStepId);
          sessionStorage.removeItem('pending_split_dialog');
        }
      } catch (e) {
        console.error('[SplitDialog] Session storage error:', e);
      }
    }
  }, [urlViewMode]);

  // Handle editing a step's config (fork and navigate to editor)
  const handleEditConfig = (stepId: string) => {
    const step = findStep(stepId);
    if (!step) {
      toast.error(t('funnel.pageNotFound'));
      return;
    }

    // Get instanceId from either standard step or first variant of split step
    let instanceId: string | undefined;
    if (step.variants && step.variants.length > 0) {
      instanceId = step.variants[0].config?.instanceId;
    } else {
      instanceId = step.config?.instanceId;
    }

    if (!instanceId) {
      toast.error(t('funnel.selectDesignFirst'));
      return;
    }

    // Call fork-for-edit mutation
    forkForEditMutation.mutate({
      sourceInstanceId: instanceId,
      storeId: id || '',
    });
  };

  // Handle opening split dialog for a step
  const handleOpenSplitDialog = (stepId: string) => {
    // If in production mode, switch to staging first
    if (urlViewMode === 'production') {
      toast.success(t('funnel.switchedToStaging'));

      // Persist action via SessionStorage (immune to URL race conditions)
      try {
        sessionStorage.setItem('pending_split_dialog', stepId);
      } catch (e) {
        console.error('Failed to save to sessionStorage', e);
      }

      setUrlViewMode('staging');
      return;
    }
    setSplitDialogStepId(stepId);
  };

  // ─── Visual Editor (Studio) handlers ──────────────────────────────

  /** Open the visual editor (Studio) for a step.
   * Works for any step — if it has Studio source data, opens in edit mode.
   * If not, opens in create mode so the user can build a new design. */
  const handleOpenVisualEditor = async (stepId: string, explicitPluginId?: string, explicitDeploymentId?: string, explicitPagePath?: string, variantId?: string) => {
    const step = findStep(stepId);
    if (!step) {
      toast.error(t('funnel.stepNotFound', { stepId }));
      return;
    }

    // The Studio overlay only renders in staging, while the header hides
    // whenever a step is open for editing — so opening the editor from the
    // production view used to leave a chrome-less page with nothing but the
    // canvas, and no way back (the view toggle lives in the hidden header).
    // Editing always means staging: switch the view instead of stranding.
    // Reachable via the ?editor=studio&env=production URL restore path even
    // if no button offers it.
    if (urlViewMode === 'production') setUrlViewMode('staging');

    // A/B split steps keep their designs in variants[].config (the main config
    // may not exist at all) — resolve the config actually being edited. Callers
    // that know the selected variant pass its id; otherwise default to the
    // first variant, matching handleEditDesign. (TAG-1334 gap b)
    const targetVariant = step.variants?.length
      ? (variantId ? step.variants.find(v => v.id === variantId) : step.variants[0])
      : undefined;
    const targetVariantId = targetVariant?.id;
    const designConfig = (targetVariant?.config ?? step.config) as any;

    // Lazy fork-on-edit: a node duplicated from another funnel still points at the
    // SOURCE funnel's shared design (marked with config.metadata.pendingFork).
    // Fork it into an independent copy BEFORE opening, so edits here can never
    // pollute the original. Server-side is idempotent (no marker => no-op), and an
    // explicit deployment (a deliberate design switch) bypasses this — UNLESS the
    // explicit deployment IS the pendingFork one: that's "edit my current (still
    // shared) design" coming from a design card, which must fork too. (TAG-1305)
    const pendingFork = designConfig?.metadata?.pendingFork;
    let forkedPluginId: string | undefined;
    let forkedDeploymentId: string | undefined;
    let forkedPagePath: string | undefined;
    if (pendingFork && (!explicitDeploymentId || explicitDeploymentId === pendingFork.deploymentId)) {
      try {
        toast.loading('Preparing your own copy…', { id: 'fork-on-edit' });
        const r = await forkNodeDesignMutation.mutateAsync({ storeId: id || '', funnelId: funnelId || '', nodeId: stepId, variantId: targetVariantId });
        toast.dismiss('fork-on-edit');
        if (r?.forked) {
          forkedPluginId = r.pluginId || undefined;
          forkedDeploymentId = r.deploymentId || undefined;
          forkedPagePath = r.pagePath || undefined;
          await refetch(); // pull the repointed node into local funnel state
        }
      } catch (e) {
        toast.dismiss('fork-on-edit');
        console.warn('[VisualEditor] fork-on-edit failed, opening shared design:', e);
      }
    }

    // A design served by other stores too: warn, and offer to swap a copy into
    // this step. Only for the step's own design — an explicit switch to another
    // one has no copy to swap in. (TAG-2859)
    const currentPluginId = designConfig?.pluginId || designConfig?.metadata?.pluginId;
    if (!forkedPluginId && currentPluginId && (!explicitPluginId || explicitPluginId === currentPluginId)) {
      const usage = await utils.funnels.getDesignUsage.fetch({ pluginId: currentPluginId }).catch(() => null);
      if (usage && usage.storeCount > 1) {
        const choice = await askSharedDesign(usage.stores);
        if (choice === 'cancel') return;
        if (choice === 'copy') {
          try {
            toast.loading(t('funnel.sharedDesignCopying'), { id: 'shared-design-copy' });
            const r = await forkNodeDesignMutation.mutateAsync({ storeId: id || '', funnelId: funnelId || '', nodeId: stepId, variantId: targetVariantId, force: true });
            toast.dismiss('shared-design-copy');
            if (!r?.forked) throw new Error('forkNodeDesign returned no copy');
            forkedPluginId = r.pluginId || undefined;
            forkedDeploymentId = r.deploymentId || undefined;
            forkedPagePath = r.pagePath || undefined;
            await refetch();
            utils.funnels.getDesignUsage.invalidate();
            // Refetch, not invalidate: this list is persisted to localStorage with
            // its fetch time, so a stale copy survives a reload and sends the next
            // Edit to the config editor instead of Studio.
            await utils.plugins.getAvailableInstancesForStepType.refetch();
          } catch (e) {
            toast.dismiss('shared-design-copy');
            toast.error(t('funnel.sharedDesignCopyFailed'));
            console.warn('[VisualEditor] shared design copy failed:', e);
            return;
          }
        }
      }
    }

    // Resolve the deployment from the RUNTIME instance — the single source of
    // truth for what the funnel actually mounts. `instanceId` is
    // `${deploymentId}-${configHash}`, so the deployment is the slice before the
    // first '-'. Trusting metadata.deploymentId here is what let a stale editor
    // pointer survive a desync and reopen an old version (TAG-1305); metadata is
    // now only a fallback for legacy nodes that never carried an instanceId.
    // Explicit args (an intentional design switch from the DesignPicker) win.
    const runtimeInstanceId = designConfig?.instanceId;
    const runtimeDashIdx = typeof runtimeInstanceId === 'string' ? runtimeInstanceId.indexOf('-') : -1;
    const runtimeDeploymentId = runtimeDashIdx > 0 ? runtimeInstanceId.slice(0, runtimeDashIdx) : undefined;

    // A fork above supersedes the (stale) local pointers — forked ids outrank
    // everything derived from designConfig.
    const pluginId = forkedPluginId || explicitPluginId || designConfig?.pluginId || designConfig?.metadata?.pluginId;
    const deploymentId = forkedDeploymentId || explicitDeploymentId || (forkedPluginId ? undefined : runtimeDeploymentId) || designConfig?.metadata?.deploymentId || designConfig?.deploymentId;
    const hasExistingPlugin = !!pluginId && !!deploymentId;

    setVisualEditorMode(hasExistingPlugin ? 'edit' : 'create');
    setVisualEditorDesign(hasExistingPlugin ? {
      pluginId: pluginId!,
      deploymentId: deploymentId!,
      pagePath: forkedPagePath || explicitPagePath || designConfig?.metadata?.internalPath,
    } : null);
    setVisualEditorStepId(stepId);
    setVisualEditorVariantId(targetVariantId ?? null);
    setSelectedStepId(null);
    setBuilderSessionToken('');
    setBuilderSessionError(false);

    requestAnimationFrame(() => {
      setEditorParams({
        editorStep: stepId,
        editorMode: hasExistingPlugin ? 'edit' : 'create',
      });
    });

    createBuilderSession.mutateAsync({
      pluginId: pluginId || '',
      deploymentId: deploymentId || '',
      storeId: id || '',
    }).then((session: { token: string }) => {
      setBuilderSessionToken(session.token);
    }).catch((err: unknown) => {
      console.warn('[VisualEditor] Failed to create builder session:', err);
      setBuilderSessionError(true);
    });
  };

  /** Create a new design from scratch using Studio visual builder */
  const handleCreateWithStudio = (stepId: string) => {
    const step = findStep(stepId);
    if (!step) return;

    // Editing is a staging-only operation (see handleOpenVisualEditor).
    if (urlViewMode === 'production') setUrlViewMode('staging');

    const randomSuffix = Math.random().toString(36).substring(2, 6);
    const suggestedName = `${step.label || step.type} ${randomSuffix}`;

    setVisualEditorMode('create');
    setVisualEditorStepId(stepId);
    setVisualEditorVariantId(null);
    setVisualEditorSuggestedName(suggestedName);
    setSelectedStepId(null);
    setBuilderSessionToken('');
    setBuilderSessionError(false);

    requestAnimationFrame(() => {
      setEditorParams({
        editorStep: stepId,
        editorMode: 'create',
      });
    });

    createBuilderSession.mutateAsync({
      storeId: id || '',
    }).then((session: { token: string }) => {
      setBuilderSessionToken(session.token);
    }).catch((err: unknown) => {
      console.warn('[VisualEditor:Create] Failed to create builder session:', err);
      setBuilderSessionError(true);
    });
  };

  // Restore visual editor from URL (e.g. shared link or page refresh)
  useEffect(() => {
    if (!pendingRestore || visualEditorStepId) return;

    consumeRestore();

    if (pendingRestore.mode === 'create') {
      handleCreateWithStudio(pendingRestore.stepId);
    } else {
      // Recover the edited A/B variant from StepEditorPanel's ?variant= index
      // param, so a mid-edit refresh doesn't silently retarget variants[0].
      const step = findStep(pendingRestore.stepId);
      const variantIdx = parseInt(new URLSearchParams(location.search).get('variant') || '0', 10);
      const restoredVariantId = step?.variants?.[Number.isNaN(variantIdx) ? 0 : variantIdx]?.id;
      handleOpenVisualEditor(pendingRestore.stepId, undefined, undefined, undefined, restoredVariantId);
    }
  }, [pendingRestore, visualEditorStepId]);

  /** Handle save result from the visual editor iframe.
   * Studio already deployed via public API. We just update the step config
   * to point to the new deployment/instance. */
  const handleBuilderSave = async (payload: SaveMessage['payload']) => {
    if (!visualEditorStepId) return;

    const step = findStep(visualEditorStepId);
    if (!step) return;

    // A/B split steps: the design belongs to ONE variant's config. Target the
    // variant the editor was opened on (fall back to the first — split steps
    // may have no main config, and writing a fragment there corrupts the step,
    // TAG-1334 gap b).
    const savedVariant = step.variants?.length
      ? (step.variants.find(v => v.id === visualEditorVariantId) ?? step.variants[0])
      : undefined;
    const baseConfig = (savedVariant?.config ?? step.config) as typeof step.config;

    // Keep the RUNTIME pointer (config.pluginId/instanceId) and the EDITOR
    // pointer (metadata.pluginId/deploymentId) in lockstep — write both from the
    // SAME deploy result. Previously config.pluginId was never updated, so a
    // save that changed the plugin lineage left the two pointers diverged and the
    // editor re-opened a stale deployment (TAG-1305). Guard with the existing
    // values so an absent payload field never nulls a pointer.
    const targetDeploymentId = payload.deploymentId || baseConfig?.metadata?.deploymentId;
    const currentInstanceId = baseConfig?.instanceId;
    let resolvedInstanceId = payload.instanceId || currentInstanceId;
    // Version: keep the displayed/served version in sync with the deployment
    // Studio just pushed (else the steps page froze at the duplicate-time '1.0.0'
    // while the builder showed the live bumped version). Guard so an absent
    // payload version never clears an existing value.
    let resolvedInstanceVersion = payload.version || baseConfig?.instanceVersion;

    // RUNTIME-POINTER LOCKSTEP (TAG-1334): an overwrite / new-version Studio save
    // can mint a NEW deployment but return no instanceId. Without this the runtime
    // pointer keeps mounting the OLD deployment while the editor pointer moves on,
    // so the funnel serves stale content the merchant can't see and every save
    // re-opens the desync. When the deployment Studio just produced differs from
    // the one our current instanceId belongs to, instantiate it (carrying the
    // current instance's config) and adopt that instanceId so runtime tracks the
    // editor. reuseIfExists makes this idempotent. Best-effort: on failure we keep
    // the existing instanceId rather than break the save.
    const deploymentOf = (iid?: string) =>
      iid && iid.includes('-') ? iid.slice(0, iid.lastIndexOf('-')) : iid;
    if (
      !payload.instanceId &&
      id &&
      targetDeploymentId &&
      currentInstanceId &&
      deploymentOf(currentInstanceId) !== targetDeploymentId
    ) {
      try {
        const current = await utils.client.plugins.getInstanceById.query({ instanceId: currentInstanceId });
        const carriedConfig = ((current as any)?.config ?? {}) as Record<string, unknown>;
        const result = await utils.client.plugins.instantiate.mutate({
          deploymentId: targetDeploymentId,
          storeId: id,
          config: carriedConfig,
          reuseIfExists: true,
        });
        if (result?.instanceId) {
          resolvedInstanceId = result.instanceId;
          resolvedInstanceVersion = payload.version || resolvedInstanceVersion;
        }
      } catch (err) {
        console.error('[FunnelEditor] runtime-pointer lockstep failed; keeping existing instanceId', err);
      }
    }

    // PAGE PATH (TAG-1946): the step config MUST carry a path or server
    // validation rejects it (isStepConfigComplete), which disabled Save forever
    // on a Studio-built step while the DESIGNS panel still showed it green. The
    // deploy result ids cannot supply it — a multi-page plugin serves every page
    // from one instance — so Studio now sends the path it just deployed. Fall
    // back to the store's instance catalog for a Studio deployed before that
    // field shipped. Best-effort: a failed lookup leaves the path unset rather
    // than losing the save.
    let resolvedPagePath = payload.pagePath;
    if (!resolvedPagePath && !baseConfig?.pagePath && !baseConfig?.metadata?.internalPath && id && targetDeploymentId) {
      try {
        // Ask the deployment, not the step-type catalog: the catalog filters
        // pages by the step's type and so answers nothing for a page built from
        // scratch in Studio, whose blank seed is a `landing` page even on an
        // `offer` step. The vanilla client also bypasses the query cache, which
        // still holds a catalog older than the deploy that just happened.
        const deployment = await utils.client.plugins.getDeploymentDetails.query({
          deploymentId: targetDeploymentId,
          storeId: id,
        });
        resolvedPagePath = resolveDeployedPagePath((deployment as any)?.pages);
      } catch (err) {
        console.error('[FunnelEditor] page-path resolution failed; step config keeps no path', err);
      }
    }

    const updatedConfig = buildStudioSaveConfig(baseConfig, {
      pluginId: payload.pluginId,
      deploymentId: targetDeploymentId,
      instanceId: resolvedInstanceId,
      instanceVersion: resolvedInstanceVersion,
      pagePath: resolvedPagePath,
    });

    // Variant save: replace the variant's config in place, main config untouched.
    const applyToVariants = (variants: any[] | undefined) =>
      (variants || []).map(v => v.id === savedVariant!.id ? { ...v, config: updatedConfig } : v);

    setFunnel((prev) => ({
      ...prev,
      steps: (prev.steps || []).map(s => {
        if (s.id !== visualEditorStepId) return s;
        return savedVariant ? { ...s, variants: applyToVariants(s.variants) } : { ...s, config: updatedConfig };
      }),
      nodes: (prev.nodes || []).map(n => {
        if (n.id !== visualEditorStepId) return n;
        // Keep the existing previewScreenshot — it's the merchant's OWN page
        // (previous version), which beats the fallback it would otherwise
        // regress to: deployment demoPreviews are editor renders with no
        // session data (inherently skeletal for commerce pages) and are
        // preserved stale across deploys whenever Studio's preview
        // generation fails. An earlier iteration deleted the capture here,
        // which made the canvas/design card flash a generic months-old
        // render after every Studio save (TAG-1804 follow-up).
        // designSavedAt is what actually refreshes the shot: the save-side
        // capture gate treats a changed stamp as "recapture this step" even
        // when the config JSON is identical — which it is on overwrite
        // deploys that keep the same deploymentId/instanceId — and the new
        // capture overwrites this one in place when it lands.
        const stampedMetadata = { ...(n.metadata || {}), designSavedAt: Date.now() };
        return savedVariant
          ? { ...n, variants: applyToVariants((n as any).variants), metadata: stampedMetadata }
          : { ...n, config: updatedConfig, metadata: stampedMetadata };
      }),
    }));

    // Force-refetch the slim screenshots query so updated demoPreviews appear
    // immediately (invalidate() marks stale; refetch() actually hits the server).
    // The heavy per-step-type catalogs are only invalidated — StepEditorPanel
    // refetches its own on mount, and force-refetching every cached step type
    // here cost ≤7 heavy queries per design save (audit FE-14).
    utils.plugins.getInstanceScreenshotsForStore.invalidate();
    utils.plugins.getInstanceScreenshotsForStore.refetch();
    utils.plugins.getAvailableInstancesForStepType.invalidate();

    // Clear persisted query cache so stale screenshots don't survive page reloads
    try { localStorage.removeItem('tgd-query-cache'); } catch { /* noop */ }

    setBuilderDirty(false);
    toast.success(t('funnel.designSaved'));
  };

  /** Handle preview request from the Studio iframe */
  const handleBuilderPreview = async (
    payload: RequestPreviewMessage['payload'],
  ): Promise<PreviewSessionMessage['payload']> => {
    if (!visualEditorStepId || !id || !funnelId) {
      throw new Error('Missing step, store, or funnel context');
    }

    const result = await utils.client.funnels.createPreviewSession.mutate({
      storeId: id,
      funnelId,
      stepId: visualEditorStepId,
      environment: urlViewMode,
      ...(payload.currency && { currency: payload.currency }),
      ...(payload.locale && { locale: payload.locale }),
      ...(payload.testCardPreset && { testCardPreset: payload.testCardPreset }),
      ...(payload.previewShippingCountry && { previewShippingCountry: payload.previewShippingCountry }),
      ...(payload.builderOptions && { builderOptions: payload.builderOptions }),
    });

    return {
      token: result.token,
      checkoutToken: result.checkoutToken,
      funnelSessionId: result.sessionId,
      previewUrl: result.previewUrl,
      resources: result.resources as Record<string, string> | undefined,
    };
  };

  /** Handle resources request from the Studio iframe */
  const handleBuilderResources = async (
    payload: RequestResourcesMessage['payload'],
  ): Promise<ResourcesMessage['payload']> => {
    const result: ResourcesMessage['payload'] = {}

    if (payload.types.includes('products') && id) {
      try {
        const data = await utils.client.products.list.query({
          storeId: id,
          page: 0,
          per_page: 50,
          includeVariants: true,
        })
        result.products = (data.items || []).map((p: any) => ({
          id: p.id,
          name: p.name,
          image: p.variants?.[0]?.imageUrl || undefined,
          variants: (p.variants || []).map((v: any) => ({
            id: v.id,
            name: v.name || 'Default',
            image: v.imageUrl || undefined,
            prices: (v.prices || []).map((price: any) => ({
              id: price.id,
              amount: price.currencyOptions?.[store?.baseCurrency || 'USD']?.amount || 0,
              currency: store?.baseCurrency || 'USD',
              interval: price.billingInterval || undefined,
            })),
          })),
        }))
      } catch { /* ignore product fetch errors */ }
    }

    if (payload.types.includes('offers') && id) {
      try {
        const data = await utils.client.upsells.list.query({ storeId: id })
        result.offers = (data?.upsells || []).map((o: any) => ({
          id: o.id,
          name: o.name,
          type: o.type,
        }))
      } catch { /* ignore offer fetch errors */ }
    }

    if (payload.types.includes('orders')) {
      try {
        const data = await utils.client.orders.list.query({
          sortBy: { field: 'orders.createdAt', direction: 'desc' },
          pagination: { page: 1, pageSize: 20 },
          filters: { 'orders.status': ['paid', 'partially_refunded'] },
        })
        result.orders = (data?.orders || []).map((o: any) => ({
          id: o.id,
          orderNumber: o.orderNumber || o.id,
          status: o.status,
          amount: o.amount || 0,
          currency: o.currency || 'USD',
          customerEmail: o.customer?.email || o.customerEmail,
          createdAt: o.createdAt,
        }))
      } catch { /* ignore order fetch errors */ }
    }

    return result
  }

  const handleMediaLibraryRequest = async (
    payload: { page?: number; perPage?: number; search?: string; type?: 'image' | 'video' | 'all'; folder?: string },
  ) => {
    try {
      const data = await utils.client.mediaLibrary.list.query({
        page: payload.page ?? 0,
        perPage: payload.perPage ?? 30,
        search: payload.search,
        type: payload.type ?? 'all',
        folder: payload.folder,
      })
      return {
        assets: data.assets.map((a: any) => ({
          id: a.id,
          url: a.url,
          filename: a.filename,
          mimeType: a.mimeType,
          fileSize: a.fileSize ?? undefined,
          width: a.width ?? undefined,
          height: a.height ?? undefined,
          folder: a.folder ?? undefined,
          tags: a.tags ?? undefined,
          alt: a.alt ?? undefined,
          createdAt: a.createdAt?.toISOString?.() ?? a.createdAt ?? undefined,
        })),
        pagination: data.pagination,
      }
    } catch (err) {
      console.error('[FunnelEditor] Media library fetch failed:', err)
      return { assets: [], pagination: { hasNext: false, total: 0, page: 0 } }
    }
  }

  const handleRegisterMediaAsset = async (
    payload: { url: string; filename: string; mimeType: string; fileSize?: number; width?: number; height?: number; folder?: string; tags?: string[]; alt?: string },
  ) => {
    const data = await utils.client.mediaLibrary.register.mutate({
      url: payload.url,
      filename: payload.filename,
      mimeType: payload.mimeType,
      fileSize: payload.fileSize,
      width: payload.width,
      height: payload.height,
      folder: payload.folder,
      tags: payload.tags,
      alt: payload.alt,
    })
    return { id: data.id, url: data.url }
  }

  /** Compute the current stepConfig for the step being edited in Studio */
  const visualEditorStepConfig = useMemo(() => {
    if (!visualEditorStepId) return null;
    const step = findStep(visualEditorStepId);
    // Split steps: the edited variant's stepConfig, not the (possibly absent)
    // main one (TAG-1334 gap b).
    const cfg = step?.variants?.length
      ? (step.variants.find(v => v.id === visualEditorVariantId) ?? step.variants[0])?.config
      : step?.config;
    if (!cfg?.stepConfig) return null;
    return cfg.stepConfig as Record<string, unknown>;
    // funnel.nodes included: findStep falls back to nodes for node-only steps,
    // whose stepConfig pushed to Studio went stale without it (FE-18).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visualEditorStepId, visualEditorVariantId, funnel.steps, funnel.nodes]);

  const handleEditDesign = async (stepId: string, designInfo?: { pluginId?: string; deploymentId?: string; pagePath?: string }, variantId?: string) => {
    const step = findStep(stepId);
    if (!step) {
      toast.error(t('funnel.pageNotFound'));
      return;
    }

    // A/B split steps: the design being edited belongs to ONE variant's config
    // (the panel passes the selected variant's id; default to the first, the
    // historical behavior of this handler). (TAG-1334 gap b)
    const targetVariant = step.variants?.length
      ? (variantId ? step.variants.find(v => v.id === variantId) : step.variants[0])
      : undefined;

    // Lazy fork-on-edit (TAG-1305): the design-card Edit path must not open the
    // SOURCE funnel's shared design either — same guard as
    // handleOpenVisualEditor. A pendingFork node is always a Studio design, so
    // on success we delegate straight to the visual editor with the forked ids
    // (a non-pendingFork explicit deployment bypasses its fork block — no double
    // fork). Explicit designInfo bypasses ONLY when it targets a different
    // design than the pendingFork one (a deliberate DesignPicker switch);
    // "edit my current still-shared design" from a card must fork.
    const editCfg = (targetVariant?.config ?? step.config) as any;
    const pendingFork = editCfg?.metadata?.pendingFork;
    if (pendingFork && (!designInfo || designInfo.deploymentId === pendingFork.deploymentId)) {
      try {
        toast.loading('Preparing your own copy…', { id: 'fork-on-edit' });
        const r = await forkNodeDesignMutation.mutateAsync({ storeId: id || '', funnelId: funnelId || '', nodeId: stepId, variantId: targetVariant?.id });
        toast.dismiss('fork-on-edit');
        if (r?.forked && r.pluginId && r.deploymentId) {
          await refetch();
          handleOpenVisualEditor(stepId, r.pluginId, r.deploymentId, undefined, targetVariant?.id);
          return;
        }
      } catch (e) {
        toast.dismiss('fork-on-edit');
        console.warn('[EditDesign] fork-on-edit failed, opening shared design:', e);
      }
    }

    const variantConfig = targetVariant?.config;
    const instanceId = variantConfig?.instanceId ?? step.config?.instanceId;

    // Prefer explicit design info from the DesignPicker (knows exactly which design is active)
    // over re-reading from step.config (which may be stale or point to a different page)
    let pagePath = designInfo?.pagePath || variantConfig?.metadata?.internalPath || step.config?.metadata?.internalPath;
    let pluginId = designInfo?.pluginId || variantConfig?.pluginId || variantConfig?.metadata?.pluginId || step.config?.pluginId || step.config?.metadata?.pluginId;
    let deploymentId = designInfo?.deploymentId || variantConfig?.metadata?.deploymentId || step.config?.metadata?.deploymentId;

    if (!instanceId) {
      toast.error(t('funnel.selectDesignFirst'));
      return;
    }

    let resolvedPagePath = pagePath || (step.type ? `/${step.type}` : undefined);

    try {
      const instancesData = await utils.plugins.getAvailableInstancesForStepType.fetch(
        step.type !== 'external'
          ? { storeId: id || '', stepType: step.type as any }
          : { storeId: id || '', stepType: 'custom' },
      );

      // When called without explicit designInfo (e.g. flow-node Edit Design),
      // step.config may be missing internalPath/deploymentId — resolve the active
      // design from instancesData the same way DesignPicker does, so Studio
      // (iframe/internal) deployments get detected instead of falling through
      // to the JSON config editor.
      if (!designInfo) {
        const stripParams = (p: string | undefined) => (p ?? '').replace(/\/:[^/]+/g, '');
        const cfgVariant = ((variantConfig?.variant || step.config?.variant) || '').toLowerCase();
        const cfgPath = stripParams(pagePath);

        let matchedPath: any = null;
        let matchedCfg: any = null;
        for (const pathData of (instancesData?.byPath || [])) {
          if (!pathData?.pluginId) continue;
          for (const ver of (pathData.versions || [])) {
            for (const cfg of (ver.configs || [])) {
              if (cfg.instanceId === instanceId) {
                matchedPath = pathData;
                matchedCfg = cfg;
                break;
              }
            }
            if (matchedCfg) break;
          }
          if (matchedCfg) break;
        }
        if (!matchedPath) {
          matchedPath = (instancesData?.byPath || []).find((p: any) => {
            if (p.pluginId !== pluginId) return false;
            if (cfgPath && stripParams(p.path) !== cfgPath) return false;
            return true;
          });
          matchedCfg = matchedPath?.versions?.[0]?.configs?.find((c: any) =>
            !cfgVariant || (c.configName || '').toLowerCase() === cfgVariant,
          ) || matchedPath?.versions?.[0]?.configs?.[0];
        }

        if (matchedPath) {
          pluginId = matchedPath.pluginId || pluginId;
          pagePath = matchedPath.path || pagePath;
          deploymentId = matchedCfg?.deploymentId
            || matchedPath.versions?.[0]?.deploymentId
            || matchedPath.deploymentId
            || deploymentId;
        }
      }

      resolvedPagePath = pagePath || (step.type ? `/${step.type}` : undefined);

      const pathEntry = instancesData?.byPath?.find((p: any) =>
        p.pluginId === pluginId && (resolvedPagePath ? p.path === resolvedPagePath : true)
      );
      const editorType = pathEntry?.editor?.type || null;
      const editorUrl = pathEntry?.editor?.url || undefined;

      if (editorType === 'iframe' || editorType === 'internal') {
        handleOpenVisualEditor(stepId, pluginId, deploymentId, resolvedPagePath, targetVariant?.id);
      } else if (editorType === 'external' && editorUrl) {
        const session = await createBuilderSession.mutateAsync({
          pluginId: pluginId || '',
          deploymentId: deploymentId || '',
          storeId: id || '',
        });
        const url = buildExternalEditorUrl(editorUrl, {
          pluginId,
          pageId: resolvedPagePath,
          deploymentId,
          returnUrl: window.location.href,
        }, session.token);
        window.open(url, '_blank');
      } else {
        const returnUrl = encodeURIComponent(
          `${location.pathname}${location.search ? `?${new URLSearchParams(location.search).toString()}` : ''}`
        );
        const pageParam = resolvedPagePath ? `&pagePath=${encodeURIComponent(resolvedPagePath)}` : '';
        navigateToStore(`${storeRoutes.EDITOR(instanceId)}?returnUrl=${returnUrl}${pageParam}`);
      }
    } catch (err) {
      console.error('[handleEditDesign] Failed to determine editor type, falling back to config editor:', err);
      const returnUrl = encodeURIComponent(
        `${location.pathname}${location.search ? `?${new URLSearchParams(location.search).toString()}` : ''}`
      );
      const pageParam = resolvedPagePath ? `&pagePath=${encodeURIComponent(resolvedPagePath)}` : '';
      navigateToStore(`${storeRoutes.EDITOR(instanceId)}?returnUrl=${returnUrl}${pageParam}`);
    }
  };

  // Handle preview step
  const [previewStepId, setPreviewStepId] = useState<string | null>(null);

  // Retry a failed step preview capture. Re-fires the working
  // `funnel/step.screenshot.requested` event server-side, then flips the step
  // into the existing "waiting for screenshot" state so the spinner shows and
  // getById polls every 3s until the new capture lands (or the 30s ceiling).
  const handleRetryScreenshot = async (stepId: string, variantId?: string) => {
    setWaitingForScreenshot(stepId);
    try {
      await retryStepScreenshotMutation.mutateAsync({
        funnelId: funnelId!,
        storeId: id!,
        stepId,
        variantId,
      });
    } catch (err) {
      setWaitingForScreenshot(null);
      toast.error(err instanceof Error ? err.message : t('flowStep.captureFailed'));
    }
  };

  // Is a preview capture plausibly in flight for this routable step? Drives the
  // DESIGNS-card "Capturing…" state (which only renders while the active design
  // still has no thumbnail). Both signals are time-bounded: the per-step
  // preview wait (30s) and the post-save/creation initial-screenshot poll
  // (60s ceiling) — so this can never spin forever.
  const CAPTURABLE_STEP_TYPES = useMemo(
    () => new Set(['checkout', 'thankyou', 'offer', 'upsell', 'downsell', 'landing']),
    [],
  );
  const isStepCapturing = (step: { id: string; type: string } | null | undefined): boolean => {
    if (!step || !CAPTURABLE_STEP_TYPES.has(step.type)) return false;
    return waitingForScreenshot === step.id || pollInitialScreenshots;
  };

  const handlePreviewStep = async (stepId: string) => {
    const step = findStep(stepId);
    if (!step) {
      toast.error(t('funnel.pageNotFound'));
      return;
    }

    // WooCommerce storefront: open the store URL directly
    if (step.type === 'storefront_woocommerce') {
      const wooUrl = integrationUrls['woocommerce'];
      if (wooUrl) {
        window.open(wooUrl.startsWith('http') ? wooUrl : `https://${wooUrl}`, '_blank', 'noopener,noreferrer');
      } else {
        toast.error(t('funnel.wooCommerceUrlNotFound'));
      }
      return;
    }

    // Shopify storefront: create preview session and open external URL directly
    if (step.type === 'storefront_shopify') {
      try {
        const result = await utils.client.funnels.createPreviewSession.mutate({
          storeId: id!, funnelId: funnelId!, stepId, environment: urlViewMode,
        });
        window.open(result.previewUrl, '_blank', 'noopener,noreferrer');
        toast.success(t('funnel.previewSessionCreated'));
      } catch (err) {
        console.error('[Preview] Failed to create preview session:', err);
        toast.error(t('funnel.failedCreatePreview'));
      }
      return;
    }

    const firstVariant = step.variants?.[0];
    const stepUrl = firstVariant?.config?.url ?? step.config?.url;
    const instanceId = firstVariant?.config?.instanceId ?? step.config?.instanceId;
    const internalPath = firstVariant?.config?.metadata?.internalPath ?? step.config?.metadata?.internalPath;

    if (!stepUrl) { toast.error(t('funnel.noUrlConfigured')); return; }
    if (!instanceId) { toast.error(t('funnel.noPluginInstance')); return; }

    try {
      const instance = await utils.client.plugins.getInstanceMetadata.query({ instanceId });
      const pages = instance?.deployment?.pages;

      const urlPath = internalPath
        ? internalPath.replace(/^\//, '')
        : stepUrl.split('?')[0].split('/').pop() || '';

      const page = pages?.find((p: PageDefinition) => {
        const pagePath = p.path.replace(/^\//, '');
        return pagePath === urlPath || p.path === `/${urlPath}`;
      });

      const hasNonStaticRequirements = page?.features?.some((feature: PageFeature) =>
        feature.requirements?.some((req: PageRequirement) =>
          req.from?.some((param: PageRequirementParam) => param.type !== 'static'),
        ),
      );
      const hasVariants = step.variants && step.variants.length > 0;

      if (hasNonStaticRequirements || hasVariants) {
        setPreviewStepId(stepId);
      } else {
        try {
          const result = await utils.client.funnels.createPreviewSession.mutate({
            storeId: id!, funnelId: funnelId!, stepId, environment: urlViewMode,
          });
          window.open(result.previewUrl, '_blank', 'noopener,noreferrer');
        } catch (err) {
          console.error('[Preview] Backend preview failed:', err);
          toast.error(t('funnel.failedCreatePreview'));
        }
      }
    } catch (error) {
      console.error('[Preview] Failed to check page requirements:', error);
      toast.error(t('funnel.failedLoadPageConfig'));
    }
  };

  // Filter out any undefined/null steps and ensure position exists
  const validSteps = useMemo(() => {
    if (funnel.nodes && funnel.nodes.length > 0) {
      return funnel.nodes.map((node) =>
        nodeToFlowStep(node, funnel.edges?.filter((e) => e.target === node.id) || []),
      );
    }

    if (funnel.steps && funnel.steps.length > 0) {
      return funnel.steps.filter((s) => s);
    }

    return [];
  }, [funnel.nodes, funnel.edges, funnel.steps]);

  // Find the selected step
  const selectedStep = validSteps.find((s) => s.id === selectedStepId);

  // Analytics panel element — shared between the desktop resizable split and
  // the mobile full-screen overlay so both stay in sync from one definition.
  const analyticsPanel = (
    <FunnelAnalyticsPanel
      funnelId={funnelId || ''}
      storeId={id || ''}
      steps={validSteps.map((s) => ({
        id: s.id,
        name: s.label,
        type: s.type,
        variants: s.variants?.map((v: any) => ({
          id: v.id,
          label: v.label || v.id.split('_').pop(),
        })),
      }))}
      edges={funnel.edges}
      onClose={() => { setAnalyticsMode(false); setAnalyticsFullscreen(false); }}
      isPublished={Boolean((apiFunnel as Funnel | undefined)?.productionConfig || funnel?.productionConfig)}
      selectedStepId={analyticsSelectedStepId}
      onStepSelect={setAnalyticsSelectedStepId}
      dateRange={analyticsDateRange}
      onDateRangeChange={setAnalyticsDateRange}
      isFullscreen={analyticsFullscreen}
      onToggleFullscreen={() => setAnalyticsFullscreen((v) => !v)}
    />
  );

  // Render the real UI immediately - no blocking loading states
  // Cached data will show while fresh data loads in the background
  // Individual components handle their own loading states internally
  return (
    <div className="relative flex h-screen flex-col overflow-hidden">
      {/* Mobile "requires desktop" notice — the funnel canvas editor is not usable
          on small screens. Hidden in analytics mode: the panel goes full-screen
          there and is fully usable on a phone. */}
      {!analyticsMode && (
        <div className="bg-amber-50 px-4 py-3 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200 md:hidden">
          <p className="font-medium">{t('funnel.desktopRecommended')}</p>
          <p className="mt-0.5 text-amber-800/80 dark:text-amber-200/70">
            {t('funnel.desktopRecommendedDesc')}
          </p>
        </div>
      )}
      {/* Header — hidden when visual editor is open (Studio has its own toolbar) */}
      {!isVisualEditorOpen && <FunnelHeader
        storeId={id || ''}
        funnel={funnel}
        onFunnelChange={setFunnel}
        hasUnsavedChanges={!!hasUnsavedChanges}
        onSave={() => handleSaveFunnel(urlViewMode)}
        onDiscard={discardChanges}
        isSaving={isSaving}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        urlViewMode={urlViewMode}
        onUrlViewModeChange={setUrlViewMode}
        selectedStepId={selectedStepId}
        onAddStep={
          viewMode === 'flow' && urlViewMode === 'staging' ? protectedHandlers.handleAddColumn : undefined
        }
        onAddAlternative={
          viewMode === 'flow' && selectedStep && urlViewMode === 'staging'
            ? () => protectedHandlers.handleAddConditionalStep(selectedStep.position.col)
            : undefined
        }
        showPreviews={showPreviews}
        onShowPreviewsChange={setShowPreviews}
        stepsMissingDesign={stepsMissingDesign}
        onSelectStep={setSelectedStepId}
        showAnalytics={analyticsMode}
        onShowAnalyticsChange={(show) => {
          setAnalyticsMode(show);
          if (show) {
            setSelectedStepId(null);
            setSelectedEdgeId(null);
          }
        }}
        onClearDraft={clearDraft}
        onCreateDirectLink={() => {
          setDirectLinkTargetId(null);
          setDirectLinkOpen(true);
        }}
        directLinkDisabledReason={directLinkDisabledReason}
      />}

      {/* Main Content */}
      <div className="relative flex flex-1 overflow-hidden bg-slate-50 dark:bg-slate-950">

        {/* Visual Editor Mode — Absolute overlay to prevent flicker during mount transition */}
        {isVisualEditorOpen && (
          <div className="absolute inset-0 z-30 flex bg-white dark:bg-slate-950">
              <BuilderIframe
                ref={builderIframeRef}
                builderUrl={getNodeEditorConfig(
                  (findStep(visualEditorStepId)?.type || 'custom') as any,
                  { hasStudioSource: true }
                ).iframeUrl || 'http://localhost:3003'}
                storeId={id || ''}
                funnelId={funnelId}
                stepId={visualEditorStepId}
                stepType={(findStep(visualEditorStepId)?.type || 'custom') as any}
                sessionToken={builderSessionToken}
                authError={builderSessionError}
                onRetryAuth={() => {
                  setBuilderSessionError(false);
                  const step = findStep(visualEditorStepId!);
                  const pluginId = step?.config?.metadata?.pluginId || (step?.config as any)?.pluginId || '';
                  const deploymentId = step?.config?.metadata?.deploymentId || (step?.config as any)?.deploymentId || '';
                  createBuilderSession.mutateAsync({
                    pluginId,
                    deploymentId,
                    storeId: id || '',
                  }).then((session: { token: string }) => {
                    setBuilderSessionToken(session.token);
                  }).catch(() => {
                    setBuilderSessionError(true);
                  });
                }}
                plugin={visualEditorMode === 'create' || !visualEditorDesign ? null : {
                  pluginId: visualEditorDesign.pluginId,
                  deploymentId: visualEditorDesign.deploymentId,
                  pageId: visualEditorDesign.pagePath,
                }}
                mode={visualEditorMode}
                suggestedName={visualEditorMode === 'create' ? visualEditorSuggestedName : undefined}
                onDirtyChange={setBuilderDirty}
                onSave={handleBuilderSave}
                onRequestPreview={handleBuilderPreview}
                onRequestResources={handleBuilderResources}
                onRequestMediaLibrary={handleMediaLibraryRequest}
                onRegisterMediaAsset={handleRegisterMediaAsset}
                stepConfig={visualEditorStepConfig}
                funnelContext={funnel.nodes && funnel.edges ? {
                  funnelName: funnel.name || 'Funnel',
                  steps: funnel.nodes.map((n: any) => ({
                    id: n.id,
                    name: n.name || n.label || n.id,
                    type: n.type || 'custom',
                    isDefault: n.isDefault,
                    path: n.config?.pagePath || n.config?.path,
                  })),
                  edges: funnel.edges.map((e: any) => ({
                    source: e.source,
                    target: e.target,
                  })),
                } : null}
                onNavigateStep={(targetStepId) => {
                  // Switching steps replaces the loaded document in Studio —
                  // unsaved work on the current step would be silently lost.
                  if (builderDirty && !window.confirm(t('funnel.builderUnsavedSwitchStepConfirm'))) return;
                  const step = findStep(targetStepId);
                  if (!step) { toast.error(t('funnel.stepNotFound', { stepId: targetStepId })); return; }

                  // Split steps keep designs in variants[].config — nav lands on
                  // the first variant (and records it so a save targets it).
                  const navVariant = step.variants?.length ? step.variants[0] : undefined;
                  const navCfg = (navVariant?.config ?? step.config) as any;

                  // A still-shared duplicated design (pendingFork) must not be
                  // opened in-place: fall through to the full open path, which
                  // forks first.
                  if (navCfg?.metadata?.pendingFork) {
                    handleOpenVisualEditor(targetStepId, undefined, undefined, undefined, navVariant?.id);
                    return;
                  }

                  const pluginId = navCfg?.metadata?.pluginId || navCfg?.pluginId;
                  const deploymentId = navCfg?.metadata?.deploymentId || navCfg?.deploymentId;
                  const hasPlugin = !!pluginId && !!deploymentId;
                  const newMode = hasPlugin ? 'edit' as const : 'create' as const;
                  const newDesign = hasPlugin ? { pluginId: pluginId!, deploymentId: deploymentId!, pagePath: navCfg?.metadata?.internalPath } : null;

                  setVisualEditorStepId(targetStepId);
                  setVisualEditorVariantId(navVariant?.id ?? null);
                  requestAnimationFrame(() => { setEditorParams({ editorStep: targetStepId, editorMode: newMode }) });

                  const targetStepConfig = navCfg?.stepConfig as Record<string, unknown> | null | undefined;

                  createBuilderSession.mutateAsync({
                    pluginId: pluginId || '', deploymentId: deploymentId || '', storeId: id || '',
                  }).then((session: { token: string }) => {
                    // Design/mode props update together with the fresh token,
                    // AFTER the session exists: softNavigate below runs before
                    // React re-renders, so BuilderIframe's identity guard skips
                    // the prop-driven reload (no double load, no stale-token
                    // init) — see FE-05 in STUDIO_AUDIT.md.
                    setBuilderSessionToken(session.token);
                    setVisualEditorMode(newMode);
                    setVisualEditorDesign(newDesign);
                    builderIframeRef.current?.softNavigate({
                      stepId: targetStepId,
                      stepType: (step.type || 'custom') as any,
                      sessionToken: session.token,
                      plugin: newDesign ? { pluginId: newDesign.pluginId, deploymentId: newDesign.deploymentId, pageId: newDesign.pagePath } : null,
                      mode: newMode,
                      stepConfig: targetStepConfig ?? null,
                    });
                  }).catch((err: unknown) => {
                    console.warn('[SoftNavigate] Session failed, falling back to full reload:', err);
                    handleOpenVisualEditor(targetStepId);
                  });
                }}
                onClose={() => {
                  const wasEditingStepId = visualEditorStepId;
                  setVisualEditorStepId(null);
                  setBuilderDirty(false);
                  setVisualEditorVariantId(null);
                  setVisualEditorDesign(null);
                  setVisualEditorSuggestedName(undefined);
                  setBuilderSessionToken('');
                  setBuilderSessionError(false);
                  clearEditorParams();
                  // Defer so clearEditorParams URL flush completes before
                  // useFunnelSelection writes stepId back
                  if (wasEditingStepId) {
                    setTimeout(() => setSelectedStepId(wasEditingStepId), 0);
                  }
                }}
                className="flex-1"
                allowFullscreen={false}
              />
          </div>
        )}

        {/* JSON Editor Mode - Full Width */}
        {viewMode === 'json' && (
          <JsonEditor
            jsonValue={jsonValue}
            onJsonChange={setJsonValue}
            onApply={handleApplyJson}
            onCancel={() => setViewMode('flow')}
            onCopy={handleCopyJson}
            environment={urlViewMode}
          />
        )}

        {/* Grid Editor Mode - Canvas + Step Editor */}
        {viewMode === 'visual' && (
          <div className="flex flex-1 overflow-hidden isolate">
            {/* LEFT: Grid Canvas - Dynamic width with independent scroll */}
            <div className={selectedStep ? 'min-w-0 flex-1 overflow-auto' : 'flex-1 overflow-auto'}>
              <GridEditor
                funnel={funnel}
                validSteps={validSteps}
                selectedStepId={selectedStepId}
                urlViewMode={urlViewMode}
                onStepSelect={(stepId) => {
                  // In analytics mode, update analytics selection
                  if (analyticsMode) {
                    setAnalyticsSelectedStepId(stepId);
                    return;
                  }
                  // In production mode, allow selection (for keyboard nav) but don't open editor
                  setSelectedStepId(stepId);
                }}
                onFunnelChange={setFunnel}
                onStepDelete={protectedHandlers.handleStepDelete}
                onAddConditionalStep={protectedHandlers.handleAddConditionalStep}
                onAddColumn={protectedHandlers.handleAddColumn}
                onTransformToSplit={handleOpenSplitDialog}
                onEditConfig={handleEditDesign}
                stepAnalytics={stepAnalytics}
                variantAnalytics={variantAnalytics}
              />
            </div>

            {/* RIGHT: Step Editor - Fixed width with independent vertical scroll (HIDDEN in production and analytics mode) */}
            {!analyticsMode && selectedStep && urlViewMode === 'staging' && (
              <div
                className="w-[420px] flex-shrink-0 overflow-hidden border-l border-slate-300 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
                data-step-editor
              >
                <div className="h-full overflow-y-auto overflow-x-hidden" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
                  {selectedStep.type === 'storefront_shopify' ? (
                    <ShopifyEditorPanel
                      step={selectedStep}
                      storeId={id!}
                      funnelId={funnelId!}
                      templatesWithIntegrationStatus={templatesWithIntegrationStatus || undefined}
                      onClose={() => setSelectedStepId(null)}
                      onChange={protectedHandlers.handleStepChange}
                      onPreview={() => handlePreviewStep(selectedStep.id)}
                    />
                  ) : selectedStep.type === 'storefront_woocommerce' ? (
                    <WooCommerceEditorPanel
                      step={selectedStep}
                      storeId={id!}
                      funnelId={funnelId!}
                      templatesWithIntegrationStatus={templatesWithIntegrationStatus || undefined}
                      onClose={() => setSelectedStepId(null)}
                      onChange={protectedHandlers.handleStepChange}
                      onPreview={() => handlePreviewStep(selectedStep.id)}
                    />
                  ) : (
                    <StepEditorPanel
                      step={selectedStep}
                      allSteps={validSteps}
                      templatesWithIntegrationStatus={templatesWithIntegrationStatus || undefined}
                      onClose={() => {
                        setSelectedStepId(null);
                      }}
                      onChange={protectedHandlers.handleStepChange}
                      onCopyPixelsToAllSteps={() => protectedHandlers.handleCopyPixelsToAllSteps(selectedStep.id) ?? false}
                      isFunnelPublished={Boolean((apiFunnel as Funnel | undefined)?.productionConfig || funnel?.productionConfig)}
                      funnelId={funnelId}
                                            onTransformToSplit={(variants, splitType) =>
                        protectedHandlers.handleTransformToSplit(selectedStep.id, variants, splitType)
                      }
                      onUpdateVariants={(variants, splitType) =>
                        protectedHandlers.handleUpdateVariants(selectedStep.id, variants, splitType)
                      }
                      onRemoveSplit={(variantToKeep) =>
                        protectedHandlers.handleRemoveSplit(selectedStep.id, variantToKeep)
                      }
                      onOpenVisualEditor={handleOpenVisualEditor}
                      onCreateWithStudio={handleCreateWithStudio}
                      onEditDesign={handleEditDesign}
                      onDuplicateDesign={handleDuplicateDesign}
                      isCapturing={isStepCapturing(selectedStep)}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Flow Editor Mode - Canvas + Step Editor */}
        {viewMode === 'flow' && (
          <>
          <ResizablePanelGroup direction="horizontal" className="flex-1 isolate">
            {/* LEFT: Flow Canvas - Dynamic width with React Flow's built-in scroll.
                On mobile the analytics panel becomes a full-screen overlay (below),
                so the canvas keeps the full width instead of a cramped 50/50 split. */}
            <ResizablePanel defaultSize={analyticsMode && isDesktop ? 50 : 100} minSize={30} className={selectedStep ? 'relative min-w-0' : 'relative'}>
              {/* Preview Toggle - Top Left Corner of Flow Editor (hidden when Studio is open) */}
              {!isVisualEditorOpen && setShowPreviews && (
                <div className="absolute left-4 top-4 z-40">
                  <Button
                    variant={showPreviews ? 'secondary' : 'outline'}
                    size="sm"
                    onClick={() => setShowPreviews(!showPreviews)}
                    className="h-8 gap-1.5 px-3 text-xs shadow-sm"
                    title={showPreviews ? t('funnel.hideStepPreviews') : t('funnel.showStepPreviews')}
                  >
                    {showPreviews ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                    <span>{t('funnel.previews')}</span>
                  </Button>
                </div>
              )}

              <ReactFlowProvider>
                <FunnelFlowCanvas
                  nodes={funnel.nodes}
                  edges={funnel.edges}
                  integrationUrls={integrationUrls}
                  instanceScreenshots={instanceScreenshots}
                  onUpdateNodes={(updatedNodes) => {
                    setFunnel((prev) => ({
                      ...prev,
                      nodes: updatedNodes,
                      steps: updatedNodes.map((node) =>
                        nodeToFlowStep(node, (prev.edges || []).filter((e) => e.target === node.id))
                      ),
                    }));
                  }}
                  onUpdateEdges={(updatedEdges) => {
                    setFunnel((prev) => ({
                      ...prev,
                      edges: updatedEdges,
                      steps: (prev.nodes || []).map((node) =>
                        nodeToFlowStep(node, updatedEdges.filter((e) => e.target === node.id))
                      ),
                    }));
                  }}
                  steps={validSteps}
                  selectedStepId={analyticsMode ? analyticsSelectedStepId : selectedStepId}
                  selectedEdgeId={selectedEdgeId}
                  readOnly={urlViewMode === 'production'}
                  onEditConfig={handleEditDesign}
                  onSelectStep={(stepId) => {
                    if (analyticsMode) {
                      setAnalyticsSelectedStepId(stepId);
                      return;
                    }
                    // Close visual editor when selecting a different step
                    if (visualEditorStepId && visualEditorStepId !== stepId) {
                      setVisualEditorStepId(null);
                    }
                    setSelectedStepId(stepId);
                    setSelectedEdgeId(null);
                    setInitialEditorTab(undefined);
                  }}
                  // onUpdateSteps removed - we update nodes/edges directly
                  onEditStep={(stepId, tab) => {
                    if (analyticsMode) return;
                    if (urlViewMode === 'production') {
                      toast.success(t('funnel.switchedToStaging'));
                      setUrlViewMode('staging');
                      // The editor will open after mode switch via useEffect
                      setTimeout(() => {
                        setSelectedStepId(stepId);
                        setSelectedEdgeId(null);
                        if (tab) {
                          setInitialEditorTab(`${tab}_${Date.now()}`);
                        }
                      }, 100);
                      return;
                    }
                    setSelectedEdgeId(null);

                    // Always select the step (even if already selected)
                    setSelectedStepId(stepId);

                    // If a specific tab is requested, set it with timestamp to force update
                    if (tab) {
                      // Append timestamp to force StepEditorPanel to recognize the change
                      setInitialEditorTab(`${tab}_${Date.now()}`);
                    } else {
                      setInitialEditorTab(undefined);
                    }
                  }}
                  onEditEdge={(edgeId) => {
                    setSelectedEdgeId(edgeId);
                    if (edgeId) setSelectedStepId(null);
                  }}
                  onPreviewStep={handlePreviewStep}
                  onRetryScreenshot={handleRetryScreenshot}
                  onGetCheckoutLink={(stepId) => {
                    setDirectLinkTargetId(stepId);
                    setDirectLinkOpen(true);
                  }}
                  onTransformToSplit={(stepId) => {
                    if (!ensureStagingMode()) return;
                    setSelectedStepId(stepId);
                    setSelectedEdgeId(null); // Close edge editor
                    handleOpenSplitDialog(stepId);
                  }}
                  onDuplicateDesign={(stepId) => {
                    if (!ensureStagingMode()) return;
                    handleDuplicateDesign(stepId);
                  }}
                  onDeleteStep={protectedHandlers.handleStepDelete}
                  onStepCreated={(stepId) => {
                    setSelectedStepId(stepId);
                    setSelectedEdgeId(null);
                  }}
                  onStepLabelChange={(stepId, newLabel) => {
                    setFunnel((prev) => ({
                      ...prev,
                      nodes:
                        prev.nodes?.map((node) =>
                          node.id === stepId ? { ...node, name: newLabel } : node,
                        ) || [],
                    }));
                  }}
                  stepAnalytics={stepAnalytics}
                  variantAnalytics={variantAnalytics}
                  showPreviews={showPreviews}
                  waitingForScreenshotStepId={waitingForScreenshot}
                  isPollingInitialScreenshots={pollInitialScreenshots}
                  storeType={store?.type as IntegrationProvider}
                  connectedProviders={connectedProviders}
                  stepsMissingDesign={stepsMissingDesign}
                />
              </ReactFlowProvider>
            </ResizablePanel>

            {/* RIGHT: Step Editor - Resizable panel (HIDDEN in production and analytics mode) */}
            {!analyticsMode && selectedStep && urlViewMode === 'staging' && (
              <>
                <ResizableHandle withHandle className="bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 transition-colors" />
                <ResizablePanel
                  defaultSize={30}
                  minSize={20}
                  maxSize={50}
                  className="h-full overflow-hidden bg-white shadow-2xl dark:bg-slate-900"
                  data-step-editor
                >
                  <div className="h-full overflow-y-auto overflow-x-hidden" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
                    {selectedStep.type === 'storefront_shopify' ? (
                      <ShopifyEditorPanel
                        step={selectedStep}
                        storeId={id!}
                        funnelId={funnelId!}
                        templatesWithIntegrationStatus={templatesWithIntegrationStatus || undefined}
                        onClose={() => {
                          setSelectedStepId(null);
                          setInitialEditorTab(undefined);
                        }}
                        onChange={protectedHandlers.handleStepChange}
                        onPreview={() => handlePreviewStep(selectedStep.id)}
                        onSave={async (updatedStep) => {
                          // Apply step changes then persist with routing
                          setFunnel((prev) => {
                            const newSteps = (prev.steps || []).filter(Boolean)
                              .map((s) => (s.id === updatedStep.id ? updatedStep : s));
                            const { node: updatedNode } = flowStepToNode(updatedStep);
                            const newNodes = (prev.nodes || []).map((n) =>
                              n.id === updatedStep.id ? updatedNode : n,
                            );
                            return { ...prev, steps: newSteps, nodes: newNodes };
                          });
                          // Let React flush state into funnelRef before saving
                          await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
                          await handleSaveFunnel();
                        }}
                      />
                    ) : selectedStep.type === 'storefront_woocommerce' ? (
                      <WooCommerceEditorPanel
                        step={selectedStep}
                        storeId={id!}
                        funnelId={funnelId!}
                        templatesWithIntegrationStatus={templatesWithIntegrationStatus || undefined}
                        onClose={() => {
                          setSelectedStepId(null);
                          setInitialEditorTab(undefined);
                        }}
                        onChange={protectedHandlers.handleStepChange}
                        onPreview={() => handlePreviewStep(selectedStep.id)}
                      />
                    )
                      :
                      (
                        <StepEditorPanel
                          step={selectedStep}
                          allSteps={validSteps}
                          templatesWithIntegrationStatus={templatesWithIntegrationStatus || undefined}
                          onClose={() => {
                            setSelectedStepId(null);
                            setInitialEditorTab(undefined);
                          }}
                          onChange={protectedHandlers.handleStepChange}
                          onCopyPixelsToAllSteps={() => protectedHandlers.handleCopyPixelsToAllSteps(selectedStep.id) ?? false}
                          isFunnelPublished={Boolean((apiFunnel as Funnel | undefined)?.productionConfig || funnel?.productionConfig)}
                          funnelId={funnelId}
                                                    onTransformToSplit={(variants, splitType) =>
                            protectedHandlers.handleTransformToSplit(selectedStep.id, variants, splitType)
                          }
                          onUpdateVariants={(variants, splitType) =>
                            protectedHandlers.handleUpdateVariants(selectedStep.id, variants, splitType)
                          }
                          onRemoveSplit={(variantToKeep) =>
                            protectedHandlers.handleRemoveSplit(selectedStep.id, variantToKeep)
                          }
                          scripts={scripts}
                          onScriptsChange={setScripts}
                          isLoadingScripts={isLoadingData || isFetchingFunnel}
                          initialTab={initialEditorTab}
                          onOpenVisualEditor={handleOpenVisualEditor}
                          onCreateWithStudio={handleCreateWithStudio}
                          onEditDesign={handleEditDesign}
                      onDuplicateDesign={handleDuplicateDesign}
                          isCapturing={isStepCapturing(selectedStep)}
                        />
                      )}
                  </div>
                </ResizablePanel>
              </>
            )}

            {/* RIGHT: Edge Editor - Fixed width when edge selected (only in edit mode) */}
            {!analyticsMode && selectedEdgeId &&
              (() => {
                // 🎯 New: Find edge in funnel.edges directly
                let edge = funnel.edges?.find((e) => e.id === selectedEdgeId);
                let sourceId = edge?.source;
                let targetId = edge?.target;

                // Fallback: Parse ID if edge not found via object lookup
                if (!sourceId || !targetId) {
                  if (selectedEdgeId.startsWith('edge_')) {
                    const parts = selectedEdgeId.split('_');
                    if (parts.length >= 5) {
                      sourceId = `${parts[1]}_${parts[2]}`;
                      targetId = `${parts[3]}_${parts[4]}`;
                    } else if (parts.length >= 4) {
                      sourceId = parts[1];
                      targetId = parts[2];
                    }
                  } else if (selectedEdgeId.includes('reactflow__edge-')) {
                    const match = selectedEdgeId.match(/reactflow__edge-(.+?)-(.+)/);
                    if (match) {
                      sourceId = match[1];
                      targetId = match[2];
                    }
                  }
                }

                if (!sourceId || !targetId) return null;

                const sourceStep = validSteps.find((s) => s.id === sourceId);
                const targetStep = validSteps.find((s) => s.id === targetId);
                const sourceNodeRaw = funnel.nodes?.find((n) => n.id === sourceId);
                const targetNodeRaw = funnel.nodes?.find((n) => n.id === targetId);

                if (!sourceStep && !sourceNodeRaw) return null;
                if (!targetStep && !targetNodeRaw) return null;

                // Construct FunnelNodes
                const sourceNode: FunnelNode = sourceNodeRaw || {
                  id: sourceStep!.id,
                  name: sourceStep!.label,
                  type: sourceStep!.type,
                  position: sourceStep!.flowPosition || { x: 0, y: 0 },
                  config: sourceStep!.config,
                };

                const targetNode: FunnelNode = targetNodeRaw || {
                  id: targetStep!.id,
                  name: targetStep!.label,
                  type: targetStep!.type,
                  position: targetStep!.flowPosition || { x: 0, y: 0 },
                  config: targetStep!.config,
                };

                // Create edge object if missing
                if (!edge) {
                  edge = {
                    id: selectedEdgeId,
                    source: sourceId,
                    target: targetId,
                    conditions: targetStep?.conditions || {},
                  };
                }

                return (
                  <>
                    <ResizableHandle withHandle className="bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 transition-colors" />
                    <ResizablePanel
                      defaultSize={30}
                      minSize={20}
                      maxSize={50}
                      className="h-full overflow-hidden bg-white shadow-2xl dark:bg-slate-900"
                      data-edge-editor
                      onClick={(e) => e.stopPropagation()}
                    >
                      <EdgeEditorPanel
                        edge={edge}
                        sourceNode={sourceNode}
                        targetNode={targetNode}
                        allNodes={validSteps.map(
                          (s): FunnelNode => ({
                            id: s.id,
                            name: s.label,
                            type: s.type,
                            position: s.flowPosition || { x: 0, y: 0 },
                            config: s.config,
                          }),
                        )}
                        storeId={id || ''}
                        onChange={(updatedEdge) => {
                          // 🔄 Update Edges
                          const updatedEdges = (funnel.edges || []).map((e) =>
                            e.id === edge!.id ? { ...e, ...updatedEdge } : e,
                          );

                          // 🔄 Update Steps (Legacy Sync)
                          const updatedSteps = validSteps.map((s) =>
                            s.id === targetId
                              ? {
                                ...s,
                                conditions: {
                                  ...s.conditions,
                                  when: updatedEdge.conditions?.when,
                                  priority: updatedEdge.conditions?.priority,
                                  bypassTo: updatedEdge.conditions?.bypassTo,
                                },
                              }
                              : s,
                          );
                          setFunnel({ ...funnel, edges: updatedEdges, steps: updatedSteps });
                        }}
                        onDelete={() => {
                          // 🔄 Update Edges
                          const updatedEdges = (funnel.edges || []).filter((e) => e.id !== edge!.id);

                          // 🔄 Update Steps (Legacy Sync)
                          const updatedSteps = validSteps.map((s) =>
                            s.id === targetId
                              ? {
                                ...s,
                                conditions: {
                                  ...s.conditions,
                                  from: s.conditions?.from?.filter((id) => id !== sourceId),
                                },
                              }
                              : s,
                          );
                          setFunnel({ ...funnel, edges: updatedEdges, steps: updatedSteps });
                          setSelectedEdgeId(null);
                          toast.success(t('funnel.connectionDeleted'));
                        }}
                        onClose={() => {
                          setSelectedEdgeId(null);
                        }}
                        readOnly={urlViewMode === 'production'} // Block edits in production
                      />
                    </ResizablePanel>
                  </>
                );
              })()}

            {/* RIGHT: Analytics Panel — desktop only, resizable from left border.
                On mobile it renders as a full-screen overlay (outside the group). */}
            {analyticsMode && isDesktop && !analyticsFullscreen && (
              <>
                <ResizableHandle withHandle className="bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 transition-colors" />
                <ResizablePanel
                  defaultSize={50}
                  minSize={30}
                  maxSize={70}
                  className="h-full overflow-hidden bg-white shadow-2xl dark:bg-slate-900"
                  data-analytics-panel
                >
                  {analyticsPanel}
                </ResizablePanel>
              </>
            )}
          </ResizablePanelGroup>

          {/* Analytics Panel — desktop full-screen overlay. Covers the canvas so
              the analytics (and especially the conversion breakdown) get the full
              editor width instead of the cramped 50% split. */}
          {analyticsMode && isDesktop && analyticsFullscreen && (
            <div
              className="absolute inset-0 z-20 flex flex-col bg-background"
              data-analytics-panel
            >
              {analyticsPanel}
            </div>
          )}

          {/* Analytics Panel — mobile full-screen overlay. Sits inside the
              relative main-content area (below the header), covering the
              canvas so the panel gets the full viewport width instead of a
              cramped split. */}
          {analyticsMode && !isDesktop && (
            <div
              className="absolute inset-0 z-20 flex flex-col bg-background"
              data-analytics-panel
            >
              {analyticsPanel}
            </div>
          )}
          </>
        )}
      </div>

      {/* Funnel Console (Errors & Operations) */}
      <FunnelConsole
        result={consoleResult || undefined}
        onClear={() => setConsoleResult(null)}
        onSelectStep={(stepId) => {
          setSelectedStepId(stepId);
          setSelectedEdgeId(null);
        }}
      />

      {/* Split Dialog - Triggered from action bar */}
      {splitDialogStepId &&
        (() => {
          const stepToSplit = validSteps.find((s) => s.id === splitDialogStepId);
          if (!stepToSplit) return null;

          return (
            <ManageVariantsDialog
              step={stepToSplit}
              open={true}
              onOpenChange={(open) => {
                if (!open) setSplitDialogStepId(null);
              }}
              onTransformToSplit={(variants, splitType) => {
                handleTransformToSplit(splitDialogStepId, variants, splitType);
                setSplitDialogStepId(null);
              }}
              onUpdateVariants={(variants, splitType) => {
                handleUpdateVariants(splitDialogStepId, variants, splitType);
                setSplitDialogStepId(null);
              }}
              onRemoveSplit={(variantToKeep) => {
                handleRemoveSplit(splitDialogStepId, variantToKeep);
                setSplitDialogStepId(null);
              }}
            />
          );
        })()}

      {/* Preview Requirements Dialog - Triggered from preview action */}
      {previewStepId &&
        (() => {
          const stepToPreview = validSteps.find((s) => s.id === previewStepId);
          if (!stepToPreview) return null;

          // ✅ Check if this is an A/B split step
          const hasVariants = stepToPreview.variants && stepToPreview.variants.length > 0;

          const stepUrl = hasVariants ? stepToPreview.variants?.[0]?.config?.url : stepToPreview.config?.url;

          const instanceId = hasVariants
            ? stepToPreview.variants?.[0]?.config?.instanceId
            : stepToPreview.config?.instanceId;

          const internalPath = hasVariants
            ? stepToPreview.variants?.[0]?.config?.metadata?.internalPath
            : stepToPreview.config?.metadata?.internalPath;

          // Get variant ID for static resources (use first variant when has variants)
          const variantId =
            hasVariants && stepToPreview.variants?.[0]?.id ? stepToPreview.variants[0].id : undefined;

          if (!stepUrl || !instanceId) return null;

          return (
            <PreviewRequirementsDialog
              isOpen={true}
              onClose={() => setPreviewStepId(null)}
              storeId={id || ''}
              funnelId={funnel?.id || ''}
              stepId={stepToPreview.id}
              instanceId={instanceId}
              stepUrl={stepUrl}
              internalPath={internalPath}
              onScreenshotTriggered={() => {
                setWaitingForScreenshot(stepToPreview.id);
              }}
              variants={hasVariants && stepToPreview.variants ? stepToPreview.variants : undefined}
              environment={urlViewMode} // 🎯 Pass environment (staging/production)
              variantId={variantId} // ✅ Pass variant ID for static resources lookup
            />
          );
        })()}

        {/* Direct Link Dialog — opened from the header action, or from a canvas
            node's "Get Link" shortcut which pre-targets that step. */}
        {directLinkOpen && directLinkTargets.targets.length > 0 && (
          <DirectLinkDialog
            isOpen
            onClose={() => setDirectLinkOpen(false)}
            storeId={id || ''}
            targets={directLinkTargets.targets}
            initialTargetId={directLinkTargetId ?? undefined}
            funnelId={funnelId}
          />
        )}

        {/* Duplicate Design Dialog */}
        {duplicateDialogStepId && !isDuplicating && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50" onClick={() => { setDuplicateDialogStepId(null); setDuplicateDesignName(''); }}>
            <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
              <h3 className="mb-4 text-lg font-semibold">{t('funnel.duplicateDesignTitle')}</h3>
              <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">{t('funnel.designNameLabel')}</label>
              <input
                type="text"
                value={duplicateDesignName}
                onChange={(e) => setDuplicateDesignName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && duplicateDesignName.trim()) confirmDuplicateDesign(); }}
                className="mb-4 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-slate-600 dark:bg-slate-800"
                autoFocus
                placeholder={t('funnel.enterDesignName')}
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => { setDuplicateDialogStepId(null); setDuplicateDesignName(''); }}>
                  {t('funnel.cancel')}
                </Button>
                <Button size="sm" onClick={confirmDuplicateDesign} disabled={!duplicateDesignName.trim()}>
                  {t('funnel.duplicate')}
                </Button>
              </div>
            </div>
          </div>
        )}

        <Dialog open={!!sharedDesignPrompt} onOpenChange={(open) => { if (!open) answerSharedDesign('cancel'); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t('funnel.sharedDesignTitle')}</DialogTitle>
              <DialogDescription>
                {t('funnel.sharedDesignBody', {
                  count: sharedDesignPrompt?.stores.length ?? 0,
                  stores: sharedDesignPrompt?.stores.map((store) => store.name).join(', '),
                })}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-wrap gap-2">
              <Button variant="ghost" size="sm" onClick={() => answerSharedDesign('cancel')}>
                {t('funnel.cancel')}
              </Button>
              <Button variant="outline" size="sm" onClick={() => answerSharedDesign('edit')}>
                {t('funnel.sharedDesignEditAnyway')}
              </Button>
              <Button size="sm" onClick={() => answerSharedDesign('copy')}>
                {t('funnel.sharedDesignMakeCopy')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Duplicating + Saving Overlay */}
        {isDuplicating && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60">
            <div className="flex flex-col items-center gap-4 rounded-xl bg-white px-10 py-8 shadow-2xl dark:bg-slate-900">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-primary" />
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('funnel.duplicatingAndSaving')}</p>
            </div>
          </div>
        )}
    </div>
  );
}
